import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import GestorLayout from '../../components/GestorLayout';
import { RankingFinal } from '../../components/gestor/RankingFinal';
import { LimitesSuspeitosCard } from '../../components/gestor/LimitesSuspeitos';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Popover, PopoverTrigger, PopoverContent } from '../../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../../components/ui/command';
import { SortableTh } from '../../components/ui/sortable-th';
import { useSortableList } from '../../hooks/useSortableList';
import { toast } from 'sonner';
import { 
  FileText, 
  Download, 
  Calendar, 
  TrendingUp, 
  Users, 
  Package,
  Clock,
  Target,
  ChevronDown,
  ChevronUp,
  Trophy,
  Medal,
  Award,
  User,
  Briefcase,
  Coffee,
  AlertCircle,
  PlayCircle,
  StopCircle,
  Percent,
  BarChart3,
  Timer,
  Search,
  ArrowUpDown,
  Filter,
  Layers,
  AlertTriangle,
  PauseCircle,
  Activity,
  DollarSign,
  ClipboardList,
  Wrench,
  Snowflake,
  X,
  Check
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Função para formatar segundos para "Xh Xm"
const formatTempo = (segundos) => {
  if (!segundos || segundos === 0) return '0s';
  const total = Math.round(segundos);
  if (total < 60) return `${total}s`;
  const horas = Math.floor(total / 3600);
  const minutos = Math.floor((total % 3600) / 60);
  const segs = total % 60;
  if (horas > 0) return segs > 0 ? `${horas}h ${minutos}m ${segs}s` : `${horas}h ${minutos}m`;
  return segs > 0 ? `${minutos}m ${segs}s` : `${minutos}m`;
};

// Função para formatar horas decimais para HH:MM
const formatarTempo = (horasDecimais) => {
  if (!horasDecimais || horasDecimais === 0) return '0m 0s';
  const totalSeg = Math.round(horasDecimais * 3600);
  const horas = Math.floor(totalSeg / 3600);
  const minutos = Math.floor((totalSeg % 3600) / 60);
  const segundos = totalSeg % 60;
  if (horas > 0) return `${horas}h ${minutos.toString().padStart(2, '0')}m`;
  return `${minutos}m ${segundos}s`;
};

// Função para formatar minutos decimais para Xm Ys ou Xh Ym
const formatarMinutos = (totalMinutos) => {
  if (!totalMinutos || totalMinutos === 0) return '0m 0s';
  const totalSeg = Math.round(totalMinutos * 60);
  const horas = Math.floor(totalSeg / 3600);
  const minutos = Math.floor((totalSeg % 3600) / 60);
  const segundos = totalSeg % 60;
  if (horas > 0) return `${horas}h ${minutos.toString().padStart(2, '0')}m`;
  return `${minutos}m ${segundos}s`;
};

// Barra de paginação reutilizável (10/25/50/100 por página)
function PaginationBar({ page, pageSize, total, totalPages, onPageChange, onPageSizeChange, testIdPrefix = 'pagination' }) {
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const canPrev = page > 1;
  const canNext = totalPages > 0 && page < totalPages;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 border-t bg-stone-50 text-xs" data-testid={testIdPrefix}>
      <div className="flex items-center gap-2">
        <span className="text-stone-600">Por página:</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
          className="h-7 border border-stone-200 rounded px-2 text-xs bg-white"
          data-testid={`${testIdPrefix}-size`}
        >
          {[10, 25, 50, 100].map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <span className="text-stone-500 ml-2">
          {from}–{to} de {total}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <button
          className="px-2 py-1 border border-stone-200 rounded bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-stone-100"
          onClick={() => onPageChange(1)}
          disabled={!canPrev}
          data-testid={`${testIdPrefix}-first`}
          title="Primeira página"
        >«</button>
        <button
          className="px-2 py-1 border border-stone-200 rounded bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-stone-100"
          onClick={() => onPageChange(page - 1)}
          disabled={!canPrev}
          data-testid={`${testIdPrefix}-prev`}
        >‹ Anterior</button>
        <span className="px-2 text-stone-600">
          Pág. <strong>{page}</strong> / {totalPages || 1}
        </span>
        <button
          className="px-2 py-1 border border-stone-200 rounded bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-stone-100"
          onClick={() => onPageChange(page + 1)}
          disabled={!canNext}
          data-testid={`${testIdPrefix}-next`}
        >Seguinte ›</button>
        <button
          className="px-2 py-1 border border-stone-200 rounded bg-white disabled:opacity-40 disabled:cursor-not-allowed hover:bg-stone-100"
          onClick={() => onPageChange(totalPages)}
          disabled={!canNext}
          data-testid={`${testIdPrefix}-last`}
          title="Última página"
        >»</button>
      </div>
    </div>
  );
}


// Tabela "Operações Executadas" com ordenação por coluna (asc/desc).
// Pré-calcula "Entre Tarefas" na ordem cronológica original para que o
// valor não perca significado quando o utilizador ordenar por outra coluna.
function OperacoesExecutadasTable({ operacoes, formatarMinutos }) {
  // 1) Ordenar cronologicamente por hora_inicio para calcular gaps corretos
  const enriched = useMemo(() => {
    if (!Array.isArray(operacoes) || operacoes.length === 0) return [];
    const parseTimeToSec = (t) => {
      if (!t || t === 'Em curso') return null;
      const parts = t.split(':').map(Number);
      const h = parts[0] || 0;
      const m = parts[1] || 0;
      const s = parts[2] || 0;
      return h * 3600 + m * 60 + s;
    };
    // Ordenação estável cronológica (só para o cálculo do gap)
    const cronol = [...operacoes].sort((a, b) => {
      const da = String(a.data || '');
      const db_ = String(b.data || '');
      if (da !== db_) return da.localeCompare(db_);
      const ta = parseTimeToSec(a.hora_inicio) ?? 0;
      const tb = parseTimeToSec(b.hora_inicio) ?? 0;
      return ta - tb;
    });
    // Injectar entre_tarefas_seg e entre_tarefas_label
    for (let i = 0; i < cronol.length; i++) {
      let entreTarefasSeg = null;
      if (i > 0) {
        const prev = cronol[i - 1];
        const prevFim = parseTimeToSec(prev.hora_fim);
        const curInicio = parseTimeToSec(cronol[i].hora_inicio);
        // Só faz sentido no mesmo dia
        if (prev.data === cronol[i].data && prevFim !== null && curInicio !== null) {
          const diff = curInicio - prevFim;
          if (diff > 0) entreTarefasSeg = diff;
        }
      }
      cronol[i] = {
        ...cronol[i],
        entre_tarefas_seg: entreTarefasSeg,
      };
    }
    return cronol;
  }, [operacoes]);

  // 2) Ordenação cliente-side (por coluna clicada)
  const getters = useMemo(() => ({
    data: (r) => r.data,
    operacao: (r) => `${r.operacao_codigo || ''} ${r.operacao_nome || ''}`.trim(),
    of: (r) => r.of_numero,
    lote: (r) => r.lote_codigo,
    hora_inicio: (r) => r.hora_inicio,
    hora_fim: (r) => r.hora_fim,
    quantidade: (r) => Number(r.quantidade || 0),
    tempo: (r) => Number(r.tempo_minutos || 0),
    entre_tarefas: (r) => (r.entre_tarefas_seg === null ? null : Number(r.entre_tarefas_seg)),
    tipo: (r) => r.tipo_conclusao || (r.ativo ? 'em_curso' : 'normal'),
    eficiencia: (r) => Number(r.eficiencia || 0),
  }), []);

  const { sortedItems, sortKey, sortDir, toggleSort } = useSortableList(enriched, {
    initialKey: null,
    getters,
  });

  const rows = sortKey ? sortedItems : enriched;

  const thProps = { activeKey: sortKey, dir: sortDir, onToggle: toggleSort };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-stone-500 border-b bg-stone-100">
            <SortableTh sortKey="data" {...thProps}>Data</SortableTh>
            <SortableTh sortKey="operacao" {...thProps}>Operação</SortableTh>
            <SortableTh sortKey="of" {...thProps}>OF</SortableTh>
            <SortableTh sortKey="lote" {...thProps}>Lote</SortableTh>
            <SortableTh sortKey="hora_inicio" {...thProps}>Início</SortableTh>
            <SortableTh sortKey="hora_fim" {...thProps}>Fim</SortableTh>
            <SortableTh sortKey="quantidade" {...thProps} align="right">Peças</SortableTh>
            <SortableTh sortKey="tempo" {...thProps} align="right">Tempo</SortableTh>
            <SortableTh sortKey="entre_tarefas" {...thProps}>Entre Tarefas</SortableTh>
            <SortableTh sortKey="tipo" {...thProps} align="center">Tipo</SortableTh>
            <SortableTh sortKey="eficiencia" {...thProps} align="right">Efic.</SortableTh>
          </tr>
        </thead>
        <tbody>
          {rows.map((reg, rIdx) => {
            const entreTarefas = reg.entre_tarefas_seg
              ? (reg.entre_tarefas_seg >= 60
                  ? `${Math.floor(reg.entre_tarefas_seg / 60)}m ${reg.entre_tarefas_seg % 60}s`
                  : `${reg.entre_tarefas_seg}s`)
              : null;

            const fmtTime = (t) => {
              if (!t || t === 'Em curso') return t || '-';
              return t.length >= 8 ? t : t + ':00';
            };

            return (
              <tr
                key={`reg-${reg.data}-${reg.hora_inicio}-${rIdx}`}
                className={`border-b last:border-0 ${reg.fora_horario ? 'bg-red-50' : reg.ativo ? 'bg-green-50' : ''}`}
              >
                <td className="py-2 px-3 font-mono text-stone-500">{reg.data || '-'}</td>
                <td className="py-2 px-3 font-medium">
                  <div className="flex items-center gap-1">
                    <span className="font-mono text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded font-bold">{reg.operacao_codigo || '-'}</span>
                    <span>{reg.operacao_nome || '-'}</span>
                  </div>
                  {reg.fora_horario && <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-red-100 text-red-600 font-bold">FORA HR</span>}
                </td>
                <td className="py-2 px-3">{reg.of_numero}</td>
                <td className="py-2 px-3">{reg.lote_codigo}</td>
                <td className="py-2 px-3 font-mono">{fmtTime(reg.hora_inicio)}</td>
                <td className="py-2 px-3 font-mono">{fmtTime(reg.hora_fim)}</td>
                <td className="py-2 px-3 text-right font-bold">{reg.quantidade}</td>
                <td className="py-2 px-3 text-right font-mono">{formatarMinutos(reg.tempo_minutos)}</td>
                <td className="py-2 px-3">
                  {entreTarefas ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded inline-block bg-amber-50 text-amber-700 font-medium">
                      {entreTarefas}
                    </span>
                  ) : (
                    <span className="text-stone-300">-</span>
                  )}
                </td>
                <td className="py-2 px-3 text-center">
                  {reg.tipo_conclusao === 'parcial' ? (
                    <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-orange-100 text-orange-700">Parcial</span>
                  ) : reg.tipo_conclusao === 'concluido' ? (
                    <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-green-100 text-green-700">Concluído</span>
                  ) : reg.tipo_conclusao === 'congelado' ? (
                    <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-cyan-100 text-cyan-700">Congelado</span>
                  ) : reg.tipo_conclusao === 'pontual' ? (
                    <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-purple-100 text-purple-700" data-testid="badge-pontual-diario">Pontual</span>
                  ) : reg.ativo ? (
                    <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-700">Em curso</span>
                  ) : (
                    <span className="px-1.5 py-0.5 rounded text-xs font-bold bg-stone-100 text-stone-500">Normal</span>
                  )}
                </td>
                <td className="py-2 px-3 text-right">{reg.eficiencia}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


// Tabela "Operações Globais" (agregado por operação no dia) com ordenação por coluna.
// Notifica o pai da ordem actual via onSortedChange para o export respeitar essa ordem.
function OperacoesGlobaisTable({ operacoes, formatarMinutos, onSortedChange }) {
  const getters = useMemo(() => ({
    nome: (r) => `${r.nome || ''} ${r.codigo || ''}`.trim(),
    registos: (r) => Number(r.registos || 0),
    quantidade: (r) => Number(r.quantidade || 0),
    tempo: (r) => Number(r.tempo_seg || 0),
    operadores: (r) => (r.operadores || []).join(', '),
  }), []);

  const { sortedItems, sortKey, sortDir, toggleSort } = useSortableList(operacoes || [], {
    initialKey: null,
    getters,
  });

  const rows = useMemo(() => (sortKey ? sortedItems : (operacoes || [])), [sortKey, sortedItems, operacoes]);
  useEffect(() => { if (typeof onSortedChange === 'function') onSortedChange(rows); }, [rows, onSortedChange]);

  const thProps = { activeKey: sortKey, dir: sortDir, onToggle: toggleSort };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-stone-500 border-b bg-stone-50">
            <SortableTh sortKey="nome" {...thProps}>Operação</SortableTh>
            <SortableTh sortKey="registos" {...thProps} align="right">Registos</SortableTh>
            <SortableTh sortKey="quantidade" {...thProps} align="right">Peças</SortableTh>
            <SortableTh sortKey="tempo" {...thProps} align="right">Tempo</SortableTh>
            <SortableTh sortKey="operadores" {...thProps}>Operadores</SortableTh>
          </tr>
        </thead>
        <tbody>
          {rows.map((op, idx) => (
            <tr key={op.codigo || `op-${idx}`} className="border-b last:border-0 hover:bg-stone-50">
              <td className="py-2.5 px-3 font-medium">{op.nome} <span className="text-stone-400">({op.codigo})</span></td>
              <td className="py-2.5 px-3 text-right">{op.registos}</td>
              <td className="py-2.5 px-3 text-right font-bold">{op.quantidade}</td>
              <td className="py-2.5 px-3 text-right font-mono">{formatarMinutos(op.tempo_seg / 60)}</td>
              <td className="py-2.5 px-3">{(op.operadores || []).join(', ')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// Tabela "Pausas Globais" do dia com ordenação por coluna.
function PausasGlobaisTable({ pausas, formatarMinutos, onSortedChange }) {
  const getters = useMemo(() => ({
    operador: (r) => r.operador,
    motivo: (r) => r.motivo,
    inicio: (r) => r.inicio,
    fim: (r) => r.fim,
    duracao: (r) => Number(r.duracao_minutos || 0),
  }), []);

  const { sortedItems, sortKey, sortDir, toggleSort } = useSortableList(pausas || [], {
    initialKey: null,
    getters,
  });

  const rows = useMemo(() => (sortKey ? sortedItems : (pausas || [])), [sortKey, sortedItems, pausas]);
  useEffect(() => { if (typeof onSortedChange === 'function') onSortedChange(rows); }, [rows, onSortedChange]);

  const thProps = { activeKey: sortKey, dir: sortDir, onToggle: toggleSort };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-stone-500 border-b bg-stone-50">
            <SortableTh sortKey="operador" {...thProps}>Operador</SortableTh>
            <SortableTh sortKey="motivo" {...thProps}>Motivo</SortableTh>
            <SortableTh sortKey="inicio" {...thProps}>Início</SortableTh>
            <SortableTh sortKey="fim" {...thProps}>Fim</SortableTh>
            <SortableTh sortKey="duracao" {...thProps} align="right">Duração</SortableTh>
          </tr>
        </thead>
        <tbody>
          {rows.map((p, idx) => (
            <tr key={`p-${p.operador}-${p.inicio}-${idx}`} className="border-b last:border-0">
              <td className="py-2 px-3 font-medium">{p.operador}</td>
              <td className="py-2 px-3">{p.motivo}</td>
              <td className="py-2 px-3 font-mono">{p.inicio}</td>
              <td className="py-2 px-3 font-mono">{p.fim}</td>
              <td className="py-2 px-3 text-right font-mono">{formatarMinutos(p.duracao_minutos)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


export default function RelatoriosV2() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Deep-link: /gestor/relatorios?tab=operador&operador=<id>&data=YYYY-MM-DD
  // (usado pelo atalho "Ver registos do dia" do Ranking Final)
  const deepLinkRef = useRef((() => {
    const tab = searchParams.get('tab');
    const operador = searchParams.get('operador');
    const data = searchParams.get('data');
    const ini = searchParams.get('data_inicio') || data;
    const fim = searchParams.get('data_fim') || data;
    const valid = (d) => /^\d{4}-\d{2}-\d{2}$/.test(d || '');
    return { tab, operador, ini: valid(ini) ? ini : null, fim: valid(fim) ? fim : null, pendente: tab === 'operador' && !!operador && valid(ini) };
  })());
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab');
    const validas = ['dashboard', 'dia', 'operador', 'of', 'operacao', 'pontuais', 'arranjos', 'suspeitos', 'melhores', 'rankings', 'ranking_final'];
    return validas.includes(t) ? t : 'dashboard';
  });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Filtros
  const [periodo, setPeriodo] = useState('semana');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [rankingOperadoresFiltrados, setRankingOperadoresFiltrados] = useState([]); // ids dos operadores no ranking; vazio = todos
  const [topOpsCategoriaFiltro, setTopOpsCategoriaFiltro] = useState(''); // código da categoria a filtrar nos Top Operadores; vazio = todas
  const [selectedOperador, setSelectedOperador] = useState('');
  const [selectedOf, setSelectedOf] = useState('');
  const [dataOperador, setDataOperador] = useState('');
  const [periodoOperador, setPeriodoOperador] = useState('dia'); // Período para relatório operador
  
  // Filtros para seleção de OF
  const [ofSearchTerm, setOfSearchTerm] = useState('');
  const [ofFiltroEstado, setOfFiltroEstado] = useState('em_curso');
  const [ofFiltroMarca, setOfFiltroMarca] = useState('');
  const [ofOrdenacao, setOfOrdenacao] = useState('recentes'); // recentes, antigas, estado, quantidade
  
  // Dados
  const [operadores, setOperadores] = useState([]);
  const [ofs, setOfs] = useState([]);
  const [desempenhoGeral, setDesempenhoGeral] = useState([]);
  const [desempenhoSortBy, setDesempenhoSortBy] = useState('eficiencia'); // eficiencia | ocupacao | tempo | pecas
  const [desempenhoSortDir, setDesempenhoSortDir] = useState('desc'); // desc | asc

  // Estado das tabelas globais ordenadas — mantido em sync via callback do sub-componente,
  // usado pelo export CSV/PDF para respeitar a ordem visível ao gestor.
  const [operacoesGlobaisSorted, setOperacoesGlobaisSorted] = useState(null);
  const [pausasGlobaisSorted, setPausasGlobaisSorted] = useState(null);

  // Ordenação local — backend devolve sempre por eficiência, mas o gestor escolhe a ordem.
  const desempenhoGeralOrdenado = useMemo(() => {
    const arr = [...(desempenhoGeral || [])];
    const key = {
      eficiencia: (o) => o.eficiencia_media || 0,
      ocupacao:   (o) => (o.taxa_ocupacao == null ? -1 : o.taxa_ocupacao),
      tempo:      (o) => o.total_tempo_segundos || (o.total_tempo_horas || 0) * 3600,
      pecas:      (o) => o.total_quantidade || 0,
    }[desempenhoSortBy] || ((o) => o.eficiencia_media || 0);
    const sign = desempenhoSortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => sign * (key(b) - key(a)));
    return arr;
  }, [desempenhoGeral, desempenhoSortBy, desempenhoSortDir]);
  const [relatorioOperador, setRelatorioOperador] = useState(null);
  const [relatorioOf, setRelatorioOf] = useState(null);
  const [rankings, setRankings] = useState(null);
  const [rankingPeriodo, setRankingPeriodo] = useState('semana');
  const [expandedOperador, setExpandedOperador] = useState(null);
  
  // Estado para relatório de operações
  const [relatorioOperacoes, setRelatorioOperacoes] = useState(null);
  const [operacaoExpandida, setOperacaoExpandida] = useState(null);
  const [operacoesDisponiveis, setOperacoesDisponiveis] = useState([]);
  const [selectedOperacaoFiltro, setSelectedOperacaoFiltro] = useState('');
  const [operacaoFiltroOpen, setOperacaoFiltroOpen] = useState(false);
  const [filtroOperadorDetalhe, setFiltroOperadorDetalhe] = useState('');
  
  // Estado para relatório pontuais
  const [pontuaisData, setPontuaisData] = useState([]);
  const [pontuaisFiltroOperador, setPontuaisFiltroOperador] = useState('');
  const [pontuaisFiltroOperacao, setPontuaisFiltroOperacao] = useState('');
  const [pontuaisPage, setPontuaisPage] = useState(1);
  const [pontuaisPageSize, setPontuaisPageSize] = useState(25);
  const [pontuaisTotal, setPontuaisTotal] = useState(0);
  const [pontuaisTotalPages, setPontuaisTotalPages] = useState(0);

  // Estado para aba "Suspeitos" (registos de eficiência anómala + exclusão manual do ranking)
  const [suspeitosData, setSuspeitosData] = useState([]);
  const [suspeitosPage, setSuspeitosPage] = useState(1);
  const [suspeitosPageSize, setSuspeitosPageSize] = useState(25);
  const [suspeitosTotal, setSuspeitosTotal] = useState(0);
  const [suspeitosTotalPages, setSuspeitosTotalPages] = useState(0);
  const [suspeitosIncluirExcluidos, setSuspeitosIncluirExcluidos] = useState(true);
  const [suspeitoUpdating, setSuspeitoUpdating] = useState(null); // registo_id em curso
  const [suspeitosLimites, setSuspeitosLimites] = useState({ efic_min: 20, efic_max: 250 }); // limites definidos pelo gestor

  // Estado para relatório arranjos (Lotes ARR)
  const [arranjosData, setArranjosData] = useState({ items: [], total_pecas: 0, total_minutos: 0, total_registos: 0 });
  const [arranjosFiltroOperador, setArranjosFiltroOperador] = useState('');
  const [arranjosFiltroOF, setArranjosFiltroOF] = useState('');
  const [arranjosColapsados, setArranjosColapsados] = useState(new Set()); // of_ids colapsados
  
  // Estado para relatório diário
  const [relatorioDia, setRelatorioDia] = useState(null);
  const [dataDia, setDataDia] = useState('');
  const [dataDiaFim, setDataDiaFim] = useState('');
  const [modoDia, setModoDia] = useState('hoje'); // 'hoje' | 'ontem' | 'intervalo'
  const [diaExpandido, setDiaExpandido] = useState(null);
  const [diaSubTab, setDiaSubTab] = useState({}); // {operadorId: 'operacoes'|'pausas'}
  const [diaGlobalTab, setDiaGlobalTab] = useState('operacoes'); // 'operacoes' | 'pausas' (cards globais)

  useEffect(() => {
    const hoje = new Date();
    const seteDiasAtras = new Date(hoje);
    seteDiasAtras.setDate(hoje.getDate() - 7);
    
    const dl = deepLinkRef.current;
    setDataFim(dl.fim || hoje.toISOString().split('T')[0]);
    setDataInicio(dl.ini || seteDiasAtras.toISOString().split('T')[0]);
    setDataOperador(dl.ini || hoje.toISOString().split('T')[0]);
    setDataDia(hoje.toISOString().split('T')[0]);
    if (dl.pendente) setSelectedOperador(dl.operador);
    
    loadInitialData();
  }, []);

  // Deep-link: assim que operador + datas estiverem aplicados, gerar logo o relatório
  useEffect(() => {
    const dl = deepLinkRef.current;
    if (!dl.pendente) return;
    if (activeTab === 'operador' && selectedOperador === dl.operador && dataInicio === dl.ini && dataFim === dl.fim) {
      dl.pendente = false;
      loadRelatorioOperador();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedOperador, dataInicio, dataFim]);

  useEffect(() => {
    if (activeTab === 'dashboard' && dataInicio && dataFim) {
      loadDesempenhoGeral();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, periodo, dataInicio, dataFim]);

  useEffect(() => {
    if (activeTab === 'rankings' || activeTab === 'melhores') {
      loadRankings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dataInicio, dataFim]);
  
  useEffect(() => {
    if (activeTab === 'dia' && dataDia) {
      loadRelatorioDia();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dataDia, dataDiaFim, modoDia]);

  useEffect(() => {
    if (activeTab === 'pontuais') {
      loadPontuais();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dataInicio, dataFim, pontuaisPage, pontuaisPageSize]);

  useEffect(() => {
    // Reset página ao mudar filtros de data
    if (activeTab === 'pontuais') setPontuaisPage(1);
    if (activeTab === 'suspeitos') setSuspeitosPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataInicio, dataFim]);

  useEffect(() => {
    if (activeTab === 'suspeitos') {
      loadSuspeitos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dataInicio, dataFim, suspeitosPage, suspeitosPageSize, suspeitosIncluirExcluidos]);

  useEffect(() => {
    if (activeTab === 'arranjos') {
      loadArranjos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dataInicio, dataFim]);

  const loadArranjos = async () => {
    try {
      setLoading(true);
      const params = {};
      if (dataInicio) params.data_inicio = dataInicio;
      if (dataFim) params.data_fim = dataFim;
      const res = await axios.get(`${API}/relatorios/arranjos`, { params });
      setArranjosData(res.data || { items: [], total_pecas: 0, total_minutos: 0, total_registos: 0 });
    } catch (e) { if (process.env.NODE_ENV === 'development') console.warn('Load arranjos error:', e); }
    finally { setLoading(false); }
  };

  const loadPontuais = async () => {
    try {
      setLoading(true);
      const params = {
        paginated: true,
        page: pontuaisPage,
        page_size: pontuaisPageSize,
      };
      if (dataInicio) params.data_inicio = dataInicio;
      if (dataFim) params.data_fim = dataFim;
      const res = await axios.get(`${API}/tarefas/conclusoes/lista`, { params });
      const data = res.data;
      if (Array.isArray(data)) {
        // fallback (não deveria acontecer com paginated=true)
        setPontuaisData(data);
        setPontuaisTotal(data.length);
        setPontuaisTotalPages(1);
      } else {
        setPontuaisData(data.items || []);
        setPontuaisTotal(data.total || 0);
        setPontuaisTotalPages(data.total_pages || 0);
      }
    } catch (e) { if (process.env.NODE_ENV === 'development') console.warn('Load pontuais error:', e); }
    finally { setLoading(false); }
  };

  const loadSuspeitos = async () => {
    try {
      setLoading(true);
      const params = {
        page: suspeitosPage,
        page_size: suspeitosPageSize,
        incluir_excluidos: suspeitosIncluirExcluidos,
      };
      if (dataInicio) params.data_inicio = dataInicio;
      if (dataFim) params.data_fim = dataFim;
      const res = await axios.get(`${API}/relatorios/registos-suspeitos`, { params });
      const data = res.data || {};
      setSuspeitosData(data.registos || []);
      setSuspeitosTotal(data.total || 0);
      setSuspeitosTotalPages(data.total_pages || 0);
      if (data.limites) setSuspeitosLimites(data.limites);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.warn('Load suspeitos error:', e);
    } finally { setLoading(false); }
  };

  const toggleExclusaoRanking = async (registoId, excluidoAtual) => {
    try {
      setSuspeitoUpdating(registoId);
      await axios.patch(`${API}/relatorios/registos-suspeitos/${registoId}/exclusao`, {
        excluido: !excluidoAtual,
      });
      toast.success(!excluidoAtual ? 'Registo excluído do ranking' : 'Registo re-incluído no ranking');
      loadSuspeitos();
    } catch (e) {
      toast.error('Falha ao atualizar exclusão');
      if (process.env.NODE_ENV === 'development') console.error(e);
    } finally { setSuspeitoUpdating(null); }
  };


  const loadInitialData = async () => {
    try {
      const [operadoresRes, ofsRes, operacoesRes] = await Promise.all([
        axios.get(`${API}/operadores`),
        axios.get(`${API}/ofs`),
        axios.get(`${API}/operacoes`)
      ]);
      setOperadores(operadoresRes.data.filter(o => o.perfil === 'operador'));
      setOfs(ofsRes.data);
      setOperacoesDisponiveis(operacoesRes.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  };

  const loadRelatorioDia = async () => {
    setLoading(true);
    try {
      const params = { data: dataDia };
      if (modoDia === 'intervalo' && dataDiaFim) {
        params.data_fim = dataDiaFim;
      }
      const res = await axios.get(`${API}/relatorios/diario`, { params });
      setRelatorioDia(res.data);
    } catch (error) {
      console.error('Erro ao carregar relatório diário:', error);
      toast.error('Erro ao carregar relatório diário');
    } finally {
      setLoading(false);
    }
  };

  const setModoData = (modo) => {
    const hoje = new Date().toISOString().split('T')[0];
    const ontem = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    setModoDia(modo);
    if (modo === 'hoje') {
      setDataDia(hoje);
      setDataDiaFim('');
    } else if (modo === 'ontem') {
      setDataDia(ontem);
      setDataDiaFim('');
    }
    // 'intervalo': user selects manually
  };


  const loadDesempenhoGeral = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/operadores/desempenho`, {
        params: { 
          data_inicio: dataInicio || undefined,
          data_fim: dataFim || undefined
        }
      });
      setDesempenhoGeral(res.data);
    } catch (error) {
      console.error('Erro ao carregar desempenho:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRelatorioOperador = async () => {
    if (!selectedOperador) {
      toast.error('Selecione um operador');
      return;
    }
    if (!dataInicio || !dataFim) {
      toast.error('Selecione as datas de início e fim');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/relatorios/operador/${selectedOperador}/detalhado`, {
        params: { 
          data_inicio: dataInicio,
          data_fim: dataFim
        }
      });
      setRelatorioOperador(res.data);
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
      toast.error('Erro ao carregar relatório do operador');
    } finally {
      setLoading(false);
    }
  };

  const loadRelatorioOf = async () => {
    if (!selectedOf) {
      toast.error('Selecione uma OF');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/relatorios/of/${selectedOf}/detalhado`);
      setRelatorioOf(res.data);
    } catch (error) {
      console.error('Erro ao carregar relatório:', error);
      toast.error('Erro ao carregar relatório da OF');
    } finally {
      setLoading(false);
    }
  };

  const loadRankings = async (overrideInicio = null, overrideFim = null, overrideOpIds = null) => {
    setLoading(true);
    try {
      const ini = overrideInicio !== null ? overrideInicio : dataInicio;
      const fim = overrideFim !== null ? overrideFim : dataFim;
      const opIds = overrideOpIds !== null ? overrideOpIds : rankingOperadoresFiltrados;
      const params = { 
        data_inicio: ini || undefined,
        data_fim: fim || undefined
      };
      if (opIds && opIds.length > 0) {
        params.operadores_ids = opIds.join(',');
      }
      const res = await axios.get(`${API}/relatorios/rankings`, { params });
      setRankings(res.data);
    } catch (error) {
      console.error('Erro ao carregar rankings:', error);
      toast.error('Erro ao carregar rankings');
    } finally {
      setLoading(false);
    }
  };

  const loadRelatorioOperacoes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/relatorios/operacoes`, {
        params: { 
          data_inicio: dataInicio,
          data_fim: dataFim,
          operacao_id: selectedOperacaoFiltro || undefined
        }
      });
      setRelatorioOperacoes(res.data);
    } catch (error) {
      console.error('Erro ao carregar relatório de operações:', error);
      toast.error('Erro ao carregar relatório de operações');
    } finally {
      setLoading(false);
    }
  };

  // Utilitário: constrói CSV a partir de headers + linhas (arrays de strings).
  // Escape RFC 4180: aspas duplas, com aspas se conter separador, aspas ou quebra de linha.
  const buildCSV = (headers, rows) => {
    const esc = (v) => {
      const s = v === null || v === undefined ? '' : String(v);
      if (s.includes(';') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };
    const lines = [headers.map(esc).join(';')];
    for (const r of rows) lines.push(r.map(esc).join(';'));
    // Usar BOM para o Excel reconhecer UTF-8 correctamente
    return '\uFEFF' + lines.join('\n');
  };

  // Descarrega um blob CSV como ficheiro
  const downloadCSVBlob = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const exportCSV = async () => {
    // Se estamos no relatório do DIA e temos dados carregados, gerar CSV cliente-side
    // para respeitar a ordenação escolhida pelo gestor nas tabelas.
    if (activeTab === 'dia' && relatorioDia) {
      setExporting(true);
      try {
        const opsList = operacoesGlobaisSorted && operacoesGlobaisSorted.length > 0
          ? operacoesGlobaisSorted
          : (relatorioDia.operacoes || []);
        const pausasList = pausasGlobaisSorted && pausasGlobaisSorted.length > 0
          ? pausasGlobaisSorted
          : (relatorioDia.pausas || []);

        // Secção 1: Operações Globais (na ordem visível)
        const csvOps = buildCSV(
          ['Operação (código)', 'Operação (nome)', 'Registos', 'Peças', 'Tempo (min)', 'Operadores'],
          opsList.map(op => [
            op.codigo || '',
            op.nome || '',
            op.registos ?? 0,
            op.quantidade ?? 0,
            Math.round(((op.tempo_seg || 0) / 60) * 100) / 100,
            (op.operadores || []).join(', '),
          ])
        );

        // Secção 2: Pausas (na ordem visível)
        const csvPausas = buildCSV(
          ['Operador', 'Motivo', 'Início', 'Fim', 'Duração (min)'],
          pausasList.map(p => [
            p.operador || '',
            p.motivo || '',
            p.inicio || '',
            p.fim || '',
            Math.round((p.duracao_minutos || 0) * 100) / 100,
          ])
        );

        // Descarregar 2 ficheiros (separados por secção — Excel-friendly)
        downloadCSVBlob(csvOps, `relatorio_dia_${dataDia || 'sem_data'}_operacoes.csv`);
        // Pequeno delay para o browser não bloquear o segundo download
        setTimeout(() => downloadCSVBlob(csvPausas, `relatorio_dia_${dataDia || 'sem_data'}_pausas.csv`), 300);

        toast.success('CSV exportado com sucesso!');
      } catch (error) {
        console.error('Erro export CSV Dia:', error);
        toast.error('Erro ao exportar CSV: ' + (error.message || 'desconhecido'));
      } finally {
        setExporting(false);
      }
      return;
    }

    if (!dataInicio || !dataFim) {
      toast.error('Selecione as datas de início e fim');
      return;
    }
    setExporting(true);
    try {
      const response = await axios.get(`${API}/export/producao`, {
        params: {
          data_inicio: dataInicio,
          data_fim: dataFim,
          operador_id: selectedOperador || undefined,
          of_id: selectedOf || undefined,
          formato: 'csv'
        },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_${dataInicio}_${dataFim}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV exportado com sucesso!');
    } catch (error) {
      console.error('Erro export CSV:', error);
      toast.error('Erro ao exportar CSV: ' + (error.response?.data?.detail || error.message));
    } finally {
      setExporting(false);
    }
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      toast.info('A gerar PDF...');
      // PDF estruturado para Rankings (com filtros aplicados, sem header da app)
      if (activeTab === 'rankings' || activeTab === 'melhores') {
        const { jsPDF } = await import('jspdf');
        const autoTable = (await import('jspdf-autotable')).default;
        const doc = new jsPDF('portrait', 'mm', 'a4');
        const pageW = doc.internal.pageSize.getWidth();

        // Título
        doc.setFontSize(16);
        doc.setTextColor(30, 58, 95);
        const tituloPdf = activeTab === 'melhores' ? 'Top Operadores por Operação' : 'Ranking de Operadores';
        doc.text(tituloPdf, pageW / 2, 14, { align: 'center' });

        // Subtítulo com período + nº operadores filtrados
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);
        const dias = dataInicio && dataFim ? Math.max(1, Math.round((new Date(dataFim) - new Date(dataInicio)) / 86400000) + 1) : 1;
        const periodoLabel = dataInicio === dataFim
          ? `Dia: ${dataInicio || '-'}`
          : `${dataInicio || '-'} → ${dataFim || '-'} · Média de ${dias} dias`;
        doc.text(periodoLabel, pageW / 2, 21, { align: 'center' });

        // Filtro operadores
        const opsLista = rankingOperadoresFiltrados.length > 0 && rankingOperadoresFiltrados[0] !== '__none__'
          ? `${rankingOperadoresFiltrados.length} operador(es) selecionado(s)`
          : 'Todos os operadores ativos';
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text(`Filtro: ${opsLista}`, pageW / 2, 27, { align: 'center' });

        let y = 35;

        // === MELHORES POR OPERAÇÃO (Top Operadores) ===
        if (activeTab === 'melhores' && rankings?.melhores_por_operacao?.length > 0) {
          const opsParaPdf = topOpsCategoriaFiltro
            ? rankings.melhores_por_operacao.filter(op => (op.categoria_codigo || '__sem__') === topOpsCategoriaFiltro)
            : rankings.melhores_por_operacao;
          if (topOpsCategoriaFiltro) {
            doc.setFontSize(9);
            doc.setTextColor(120, 120, 120);
            doc.text(`Categoria: ${topOpsCategoriaFiltro}`, pageW / 2, 31, { align: 'center' });
          }
          for (const op of opsParaPdf) {
            if (y > 250) { doc.addPage(); y = 15; }
            // Cabeçalho da operação: código + nome + categoria
            doc.setFontSize(11);
            doc.setTextColor(30, 58, 95);
            const codigoPart = op.operacao_codigo ? `${op.operacao_codigo} · ` : '';
            doc.text(`${codigoPart}${op.operacao_nome}`, 14, y);
            if (op.categoria_codigo) {
              doc.setFontSize(9);
              doc.setTextColor(100, 100, 100);
              const catLabel = `[${op.categoria_codigo}${op.categoria_nome ? ' · ' + op.categoria_nome : ''}]`;
              doc.text(catLabel, pageW - 14, y, { align: 'right' });
            }
            y += 3;
            // Tabela de operadores
            if (op.operadores?.length > 0) {
              autoTable(doc, {
                startY: y,
                head: [['#', 'Operador', 'Eficiência', 'Prev', 'Real', 'Peças', 'OFs']],
                body: op.operadores.map((o, i) => [
                  i + 1,
                  o.nome,
                  `${o.eficiencia_media}%`,
                  formatTempo(o.tempo_previsto || 0),
                  formatTempo(o.tempo_total || 0),
                  o.pecas_total,
                  (o.ofs || []).join(', ').substring(0, 30),
                ]),
                theme: 'striped',
                headStyles: { fillColor: [30, 58, 95], textColor: [255, 255, 255], fontSize: 8 },
                bodyStyles: { fontSize: 8 },
                columnStyles: { 0: { cellWidth: 8, halign: 'center' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' } },
                margin: { left: 14, right: 14 },
              });
              y = doc.lastAutoTable.finalY + 6;
            } else {
              doc.setFontSize(9);
              doc.setTextColor(150, 150, 150);
              doc.text('Sem dados', 14, y + 4);
              y += 8;
            }
          }
          // Footer
          const totalPagesM = doc.internal.getNumberOfPages();
          for (let i = 1; i <= totalPagesM; i++) {
            doc.setPage(i);
            doc.setFontSize(7);
            doc.setTextColor(150, 150, 150);
            doc.text(
              `Gerado em ${new Date().toLocaleString('pt-PT')} · Pág. ${i}/${totalPagesM}`,
              pageW / 2,
              doc.internal.pageSize.getHeight() - 6,
              { align: 'center' }
            );
          }
          doc.save(`top_operadores_${dataInicio || 'all'}_${dataFim || 'all'}.pdf`);
          toast.success('PDF exportado com sucesso!');
          setExporting(false);
          return;
        }

        // Ranking de Eficiência
        if (rankings?.ranking_eficiencia?.length > 0) {
          doc.setFontSize(12);
          doc.setTextColor(180, 83, 9);
          doc.text('Ranking de Eficiência (ponderada por peças)', 14, y);
          y += 4;
          autoTable(doc, {
            startY: y,
            head: [['#', 'Operador', 'Eficiência', 'Operações', 'Peças']],
            body: rankings.ranking_eficiencia.map(o => [
              o.posicao,
              o.nome,
              `${o.eficiencia_media}%`,
              o.total_operacoes,
              o.total_pecas,
            ]),
            theme: 'striped',
            headStyles: { fillColor: [217, 119, 6], textColor: [255, 255, 255], fontSize: 9 },
            bodyStyles: { fontSize: 9 },
            columnStyles: { 0: { cellWidth: 10, halign: 'center' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } },
          });
          y = doc.lastAutoTable.finalY + 8;
        }

        // Ranking de Ocupação
        if (rankings?.ranking_ocupacao?.length > 0) {
          if (y > 240) { doc.addPage(); y = 15; }
          doc.setFontSize(12);
          doc.setTextColor(22, 101, 52);
          doc.text('Ranking de Taxa de Ocupação (média diária)', 14, y);
          y += 4;
          autoTable(doc, {
            startY: y,
            head: [['#', 'Operador', 'Ocupação', 'Operações', 'Peças']],
            body: rankings.ranking_ocupacao.map(o => [
              o.posicao,
              o.nome,
              `${o.taxa_ocupacao}%`,
              o.total_operacoes,
              o.total_pecas,
            ]),
            theme: 'striped',
            headStyles: { fillColor: [22, 163, 74], textColor: [255, 255, 255], fontSize: 9 },
            bodyStyles: { fontSize: 9 },
            columnStyles: { 0: { cellWidth: 10, halign: 'center' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' } },
          });
          y = doc.lastAutoTable.finalY + 8;
        }

        // Estatísticas Gerais
        if (rankings?.estatisticas) {
          if (y > 240) { doc.addPage(); y = 15; }
          doc.setFontSize(11);
          doc.setTextColor(30, 58, 95);
          doc.text('Estatísticas Gerais', 14, y);
          y += 5;
          autoTable(doc, {
            startY: y,
            head: [['Indicador', 'Valor']],
            body: [
              ['Operadores activos', rankings.estatisticas.total_operadores ?? '-'],
              ['Total de peças', rankings.estatisticas.total_pecas_periodo ?? '-'],
              ['Eficiência média geral', `${rankings.estatisticas.eficiencia_media_geral ?? 0}%`],
              ['Ocupação média geral', `${rankings.estatisticas.ocupacao_media_geral ?? 0}%`],
            ],
            theme: 'grid',
            headStyles: { fillColor: [30, 58, 95], textColor: [255, 255, 255], fontSize: 9 },
            bodyStyles: { fontSize: 9 },
            columnStyles: { 1: { halign: 'right' } },
          });
        }

        // Footer com timestamp
        const totalPages = doc.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          doc.setPage(i);
          doc.setFontSize(7);
          doc.setTextColor(150, 150, 150);
          doc.text(
            `Gerado em ${new Date().toLocaleString('pt-PT')} · Pág. ${i}/${totalPages}`,
            pageW / 2,
            doc.internal.pageSize.getHeight() - 6,
            { align: 'center' }
          );
        }

        doc.save(`rankings_${dataInicio || 'all'}_${dataFim || 'all'}.pdf`);
        toast.success('PDF exportado com sucesso!');
        setExporting(false);
        return;
      }

      // Fallback genérico (outros tabs) — captura HTML
      const html2pdf = (await import('html2pdf.js')).default;
      
      const contentEl = document.getElementById('relatorios-content');
      if (!contentEl) {
        toast.error('Conteúdo não encontrado para exportar');
        setExporting(false);
        return;
      }
      
      // Remover badges azuis temporariamente para PDF limpo
      const badges = contentEl.querySelectorAll('.bg-blue-600, .bg-navy');
      badges.forEach(b => {
        b.dataset.origClass = b.className;
        b.className = b.className.replace(/bg-blue-600|bg-navy/g, '').trim();
        b.style.background = 'none';
        b.style.color = '#1e3a5f';
        b.style.fontWeight = 'bold';
        b.style.padding = '0';
      });
      
      const filename = `relatorio_${activeTab}_${dataInicio || 'all'}_${dataFim || 'all'}.pdf`;
      
      await html2pdf().set({
        margin: [5, 3, 5, 3],
        filename,
        image: { type: 'jpeg', quality: 0.85 },
        html2canvas: { scale: 1.2, useCORS: true, logging: false, width: contentEl.scrollWidth },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      }).from(contentEl).save();
      
      // Restaurar badges
      badges.forEach(b => {
        if (b.dataset.origClass) {
          b.className = b.dataset.origClass;
          b.style.background = '';
          b.style.color = '';
          b.style.fontWeight = '';
          b.style.padding = '';
        }
      });
      
      toast.success('PDF exportado com sucesso!');
    } catch (error) {
      console.error('Erro export PDF:', error);
      toast.error('Erro ao exportar PDF');
    } finally {
      setExporting(false);
    }
  };

  const getCorClass = (eficiencia) => {
    if (eficiencia >= 101) return 'text-blue-600 bg-blue-50';
    if (eficiencia >= 90) return 'text-green-600 bg-green-50';
    if (eficiencia >= 76) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getMedalIcon = (posicao) => {
    if (posicao === 1) return <Trophy className="h-6 w-6 text-yellow-500" />;
    if (posicao === 2) return <Medal className="h-6 w-6 text-gray-400" />;
    if (posicao === 3) return <Award className="h-6 w-6 text-amber-600" />;
    return <span className="text-lg font-bold text-stone-400">#{posicao}</span>;
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'dia', label: 'Dia', icon: Calendar },
    { id: 'operador', label: 'Por Operador', icon: User },
    { id: 'of', label: 'Por OF', icon: Briefcase },
    { id: 'operacao', label: 'Por Operação', icon: Timer },
    { id: 'pontuais', label: 'Pontuais', icon: FileText },
    { id: 'arranjos', label: 'Arranjos', icon: Wrench },
    { id: 'suspeitos', label: 'Suspeitos', icon: AlertTriangle },
    { id: 'melhores', label: 'Top Operadores', icon: Award },
    { id: 'rankings', label: 'Rankings', icon: Trophy },
    { id: 'ranking_final', label: 'Ranking Final', icon: Medal }
  ];

  return (
    <GestorLayout>
      <div className="p-3 max-w-7xl mx-auto">
        {/* Header Ultra Compacto */}
        <div className="mb-3 flex items-center justify-between">
          <h1 className="text-xl font-heading font-bold text-navy">Relatórios e Analytics</h1>
        </div>

        {/* Tabs de Navegação - Compactas */}
        <div className="bg-white rounded-lg shadow-soft p-1 mb-3 flex flex-wrap gap-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-navy text-white shadow-sm'
                  : 'text-stone-600 hover:bg-stone-100'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filtro de Datas Global — esconder na aba Dia e Operação (usam filtro próprio) */}
        {activeTab !== 'dia' && activeTab !== 'operacao' && activeTab !== 'ranking_final' && (
        <Card className="p-3 mb-3" data-testid="global-date-filter">
          <div className="flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Data Início</label>
              <input
                type="date"
                value={dataInicio}
                onChange={(e) => setDataInicio(e.target.value)}
                className="h-9 px-3 border rounded-lg text-sm"
                data-testid="global-data-inicio"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Data Fim</label>
              <input
                type="date"
                value={dataFim}
                onChange={(e) => setDataFim(e.target.value)}
                className="h-9 px-3 border rounded-lg text-sm"
                data-testid="global-data-fim"
              />
            </div>
            {activeTab === 'operador' && (
              <>
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs font-medium text-stone-600 mb-1">Operador</label>
                  <select
                    value={selectedOperador}
                    onChange={(e) => setSelectedOperador(e.target.value)}
                    className="w-full h-9 px-3 border rounded-lg bg-white text-sm"
                    data-testid="select-operador"
                  >
                    <option value="">Selecione um operador</option>
                    {operadores.map(op => (
                      <option key={op.id} value={op.id}>{op.nome_completo}</option>
                    ))}
                  </select>
                </div>
                <Button onClick={loadRelatorioOperador} disabled={loading} data-testid="gerar-relatorio-operador" className="h-9">
                  <FileText className="h-4 w-4 mr-2" />
                  Gerar Relatório
                </Button>
                {relatorioOperador && (
                  <>
                    <Button variant="outline" size="sm" onClick={exportCSV} disabled={exporting} data-testid="export-csv-btn" className="h-9">
                      <Download className="h-4 w-4 mr-1" /> CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportPDF} disabled={exporting} data-testid="export-pdf-btn" className="h-9">
                      <FileText className="h-4 w-4 mr-1" /> PDF
                    </Button>
                  </>
                )}
              </>
            )}
          </div>
        </Card>
        )}

        {/* Conteúdo dos Relatórios (usado para PDF export) */}
        <div id="relatorios-content">

        {/* TAB: DASHBOARD GERAL */}
        {activeTab === 'dashboard' && (
          <div className="space-y-4">

            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="p-4 bg-gradient-to-br from-blue-50 to-white">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-navy">{desempenhoGeral.length}</p>
                    <p className="text-sm text-stone-600">Operadores Ativos</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 bg-gradient-to-br from-green-50 to-white">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-100 rounded-xl">
                    <Package className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-navy">
                      {desempenhoGeral.reduce((acc, op) => acc + op.total_quantidade, 0)}
                    </p>
                    <p className="text-sm text-stone-600">Peças Produzidas</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 bg-gradient-to-br from-purple-50 to-white">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-purple-100 rounded-xl">
                    <Clock className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-navy">
                      {formatarTempo(desempenhoGeral.reduce((acc, op) => acc + op.total_tempo_horas, 0))}
                    </p>
                    <p className="text-sm text-stone-600">Tempo Total</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 bg-gradient-to-br from-amber-50 to-white">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-amber-100 rounded-xl">
                    <Target className="h-6 w-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-navy">
                      {desempenhoGeral.length > 0 
                        ? (desempenhoGeral.reduce((acc, op) => acc + op.eficiencia_media, 0) / desempenhoGeral.length).toFixed(1)
                        : 0}%
                    </p>
                    <p className="text-sm text-stone-600">Eficiência Média</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Lista de Operadores */}
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Users className="h-5 w-5 text-navy" />
                  Desempenho por Operador
                </h2>
                <div className="flex items-center gap-2 text-sm">
                  <label className="text-stone-600 font-medium">Ordenar por:</label>
                  <select
                    value={desempenhoSortBy}
                    onChange={(e) => setDesempenhoSortBy(e.target.value)}
                    className="px-3 py-1.5 border border-stone-300 rounded-md bg-white text-sm font-medium text-stone-700 focus:outline-none focus:ring-2 focus:ring-navy/30"
                    data-testid="desempenho-sort-by"
                  >
                    <option value="eficiencia">Eficiência</option>
                    <option value="ocupacao">Ocupação</option>
                    <option value="tempo">Tempo</option>
                    <option value="pecas">Peças</option>
                  </select>
                  <button
                    type="button"
                    onClick={() => setDesempenhoSortDir(d => d === 'desc' ? 'asc' : 'desc')}
                    title={desempenhoSortDir === 'desc' ? 'Decrescente (maior primeiro)' : 'Crescente (menor primeiro)'}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-stone-300 bg-white text-stone-700 hover:bg-stone-50 transition"
                    data-testid="desempenho-sort-dir"
                    aria-label={desempenhoSortDir === 'desc' ? 'Mudar para ordenação crescente' : 'Mudar para ordenação decrescente'}
                  >
                    {desempenhoSortDir === 'desc' ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              {loading ? (
                <div className="text-center py-8 text-stone-500">A carregar...</div>
              ) : (
                <div className="space-y-2">
                  {desempenhoGeralOrdenado.map((op, idx) => (
                    <div 
                      key={op.id}
                      className={`p-4 rounded-xl border ${getCorClass(op.eficiencia_media)} border-current/20`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-lg font-bold">#{idx + 1}</span>
                          <div>
                            <p className="font-semibold">{op.nome}</p>
                            <p className="text-sm opacity-80">
                              {op.total_registos} {op.total_registos === 1 ? 'item' : 'itens'}
                              {typeof op.total_operacoes === 'number' && (
                                <span className="ml-1 text-xs opacity-70">({op.total_operacoes} op · {op.total_pontuais || 0} tar)</span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6 text-right">
                          <div>
                            <p className="text-2xl font-bold">{op.eficiencia_media}%</p>
                            <p className="text-xs opacity-80">eficiência</p>
                          </div>
                          <div>
                            <p className="text-lg font-semibold">{op.taxa_ocupacao != null ? `${op.taxa_ocupacao}%` : '—'}</p>
                            <p className="text-xs opacity-80">ocupação</p>
                          </div>
                          <div>
                            <p className="text-lg font-semibold">{op.total_quantidade}</p>
                            <p className="text-xs opacity-80">peças</p>
                          </div>
                          <div>
                            <p className="text-lg font-semibold">{formatarTempo(op.total_tempo_horas)}</p>
                            <p className="text-xs opacity-80">tempo</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}


        {/* TAB: DIA */}
        {activeTab === 'dia' && (
          <div className="space-y-6" data-testid="tab-dia">
            {/* Seleção de data com botões rápidos */}
            <Card className="p-5">
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex gap-2">
                  <Button
                    variant={modoDia === 'hoje' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setModoData('hoje')}
                    data-testid="btn-hoje"
                  >
                    Hoje
                  </Button>
                  <Button
                    variant={modoDia === 'ontem' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setModoData('ontem')}
                    data-testid="btn-ontem"
                  >
                    Ontem
                  </Button>
                  <Button
                    variant={modoDia === 'intervalo' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setModoData('intervalo')}
                    data-testid="btn-intervalo"
                  >
                    Intervalo
                  </Button>
                </div>

                {modoDia === 'intervalo' ? (
                  <div className="flex gap-2 items-end">
                    <div>
                      <label className="text-xs font-semibold text-stone-600 block mb-1">De</label>
                      <Input type="date" value={dataDia} onChange={(e) => setDataDia(e.target.value)} className="w-40" data-testid="input-data-dia" />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-stone-600 block mb-1">Até</label>
                      <Input type="date" value={dataDiaFim} onChange={(e) => setDataDiaFim(e.target.value)} className="w-40" data-testid="input-data-dia-fim" />
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="text-xs font-semibold text-stone-600 block mb-1">Data</label>
                    <Input type="date" value={dataDia} onChange={(e) => { setDataDia(e.target.value); setModoDia(''); }} className="w-44" data-testid="input-data-dia" />
                  </div>
                )}

                <Button onClick={loadRelatorioDia} disabled={loading} variant="default" data-testid="btn-gerar-dia">
                  {loading ? 'A carregar...' : 'Gerar Relatório'}
                </Button>
                {relatorioDia && (
                  <>
                    <Button variant="outline" size="sm" onClick={exportCSV} disabled={exporting} data-testid="export-csv-dia">
                      <Download className="h-4 w-4 mr-1" /> CSV
                    </Button>
                    <Button variant="outline" size="sm" onClick={exportPDF} disabled={exporting} data-testid="export-pdf-dia">
                      <FileText className="h-4 w-4 mr-1" /> PDF
                    </Button>
                  </>
                )}
              </div>
              {relatorioDia?.num_dias > 1 && (
                <p className="text-xs text-stone-500 mt-2">Relatório de {relatorioDia.num_dias} dias ({relatorioDia.data} a {relatorioDia.data_fim})</p>
              )}
            </Card>

            {relatorioDia && (
              <>
                {/* KPIs separados: Peças vs Operações */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                  <Card className="p-4 text-center">
                    <Layers className="h-5 w-5 mx-auto text-blue-600 mb-1" />
                    <p className="text-xl font-bold">{relatorioDia.resumo.total_pecas_unicas || 0}</p>
                    <p className="text-xs text-stone-500">Peças Produzidas</p>
                    <p className="text-[10px] text-stone-400">(por lote)</p>
                  </Card>
                  <Card className="p-4 text-center">
                    <Activity className="h-5 w-5 mx-auto text-indigo-600 mb-1" />
                    <p className="text-xl font-bold">{relatorioDia.resumo.total_operacoes || 0}</p>
                    <p className="text-xs text-stone-500">Operações Feitas</p>
                  </Card>
                  <Card className="p-4 text-center">
                    <Clock className="h-5 w-5 mx-auto text-emerald-600 mb-1" />
                    <p className="text-xl font-bold">{formatarTempo(relatorioDia.resumo.total_tempo_produtivo_horas)}</p>
                    <p className="text-xs text-stone-500">Tempo Produtivo</p>
                  </Card>
                  <Card className="p-4 text-center">
                    <AlertTriangle className="h-5 w-5 mx-auto text-amber-500 mb-1" />
                    <p className="text-xl font-bold">{formatarTempo(relatorioDia.resumo.total_tempo_morto_horas)}</p>
                    <p className="text-xs text-stone-500">Tempo Morto</p>
                  </Card>
                  <Card className="p-4 text-center">
                    <Percent className="h-5 w-5 mx-auto text-purple-600 mb-1" />
                    <p className="text-xl font-bold">{relatorioDia.resumo.eficiencia_media_percentual}%</p>
                    <p className="text-xs text-stone-500">Eficiência Média</p>
                  </Card>
                  <Card className="p-4 text-center">
                    <Users className="h-5 w-5 mx-auto text-teal-600 mb-1" />
                    <p className="text-xl font-bold">{relatorioDia.resumo.total_operadores}</p>
                    <p className="text-xs text-stone-500">Operadores Ativos</p>
                  </Card>
                  <Card className="p-4 text-center">
                    <TrendingUp className="h-5 w-5 mx-auto text-sky-600 mb-1" />
                    <p className="text-xl font-bold">{relatorioDia.resumo.produtividade_hora}</p>
                    <p className="text-xs text-stone-500">Peças/Hora</p>
                  </Card>
                </div>

                {/* Resumo extra */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card className="p-4 text-center">
                    <Users className="h-5 w-5 mx-auto text-stone-600 mb-1" />
                    <p className="text-xl font-bold">{relatorioDia.resumo.total_operadores}</p>
                    <p className="text-xs text-stone-500">Operadores</p>
                  </Card>
                  <Card className="p-4 text-center">
                    <FileText className="h-5 w-5 mx-auto text-stone-600 mb-1" />
                    <p className="text-xl font-bold">{relatorioDia.resumo.total_registos}</p>
                    <p className="text-xs text-stone-500">Registos</p>
                  </Card>
                  <Card className="p-4 text-center">
                    <Activity className="h-5 w-5 mx-auto text-green-600 mb-1" />
                    <p className="text-xl font-bold">{relatorioDia.resumo.total_em_curso}</p>
                    <p className="text-xs text-stone-500">Em Curso</p>
                  </Card>
                  <Card className="p-4 text-center">
                    <DollarSign className="h-5 w-5 mx-auto text-stone-600 mb-1" />
                    <p className="text-xl font-bold">{relatorioDia.resumo.total_custo_mao_obra.toFixed(2)}€</p>
                    <p className="text-xs text-stone-500">Custo M.O.</p>
                  </Card>
                </div>

                {/* Operadores do dia */}
                <Card className="p-5">
                  <h3 className="font-semibold text-sm mb-4 flex items-center gap-2">
                    <Users className="h-4 w-4" /> Operadores ({relatorioDia.operadores.length})
                  </h3>
                  {relatorioDia.operadores.length === 0 ? (
                    <p className="text-stone-400 text-sm text-center py-6">Sem registos neste dia</p>
                  ) : (
                    <div className="space-y-3">
                      {relatorioDia.operadores.map((op, idx) => (
                        <div key={op.operador || `op-${idx}`} className="border rounded-xl overflow-hidden">
                          <button
                            onClick={() => setDiaExpandido(diaExpandido === idx ? null : idx)}
                            className="w-full flex items-center justify-between p-4 hover:bg-stone-50 transition-colors text-left"
                            data-testid={`dia-operador-${idx}`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-8 h-8 rounded-full bg-stone-800 text-white flex items-center justify-center text-xs font-bold shrink-0">
                                {(op.operador || '?')[0]}
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-sm truncate">{op.operador}</p>
                                <p className="text-xs text-stone-500">{op.total_registos} ops · {op.quantidade_produzida} peças · {op.eficiencia_media}% efic.</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 text-right shrink-0">
                              <div>
                                <p className="font-mono text-sm font-bold text-emerald-600">{formatarMinutos(op.tempos?.em_operacoes_min || 0)}</p>
                                <p className="text-[10px] text-stone-400">em ops</p>
                              </div>
                              <div>
                                <p className="font-mono text-sm font-bold text-amber-600">{formatarMinutos(op.tempos?.tempos_mortos_min || 0)}</p>
                                <p className="text-[10px] text-stone-400">morto</p>
                              </div>
                              <div>
                                <p className="font-mono text-sm font-bold text-violet-600">{formatarMinutos(op.tempos?.pausas_legais_min || 0)}</p>
                                <p className="text-[10px] text-stone-400">legais</p>
                              </div>
                              <div>
                                <p className="font-mono text-sm font-bold text-red-500">{formatarMinutos(op.tempos?.fora_horario_total_min || 0)}</p>
                                <p className="text-[10px] text-stone-400">fora hr</p>
                              </div>
                              <ChevronDown className={`h-4 w-4 text-stone-400 transition-transform ${diaExpandido === idx ? 'rotate-180' : ''}`} />
                            </div>
                          </button>

                          {diaExpandido === idx && (
                            <div className="bg-stone-50 border-t">
                              {/* Resumo de tempos — Dentro do Horário */}
                              <div className="px-4 pt-4 pb-2">
                                <p className="text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">Dentro do Horário Laboral</p>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                  <div className="bg-white rounded-lg p-3 text-center border">
                                    <p className="text-lg font-bold text-emerald-600">{formatarMinutos(op.tempos?.em_operacoes_min || 0)}</p>
                                    <p className="text-[10px] text-stone-500 font-medium">Em Operações</p>
                                  </div>
                                  <div className="bg-white rounded-lg p-3 text-center border">
                                    <p className="text-lg font-bold text-amber-600">{formatarMinutos(op.tempos?.tempos_mortos_min || 0)}</p>
                                    <p className="text-[10px] text-stone-500 font-medium">Tempos Mortos</p>
                                    {(op.tempos?.pausas_manuais_min || 0) > 0 && (
                                      <p className="text-[9px] text-orange-500 mt-0.5">({formatarMinutos(op.tempos?.pausas_manuais_min || 0)} pausas manuais)</p>
                                    )}
                                    {(op.tempos?.congeladas_min || 0) > 0 && (
                                      <p className="text-[9px] text-cyan-600 mt-0.5 italic" title="Não contam como tempo morto">
                                        + {formatarMinutos(op.tempos?.congeladas_min || 0)} em congelamento
                                      </p>
                                    )}
                                  </div>
                                  <div className="bg-violet-50 rounded-lg p-3 text-center border border-violet-200">
                                    <p className="text-lg font-bold text-violet-700">{formatarMinutos(op.tempos?.pausas_legais_min || 0)}</p>
                                    <p className="text-[10px] text-violet-600 font-medium">Pausas Legais</p>
                                  </div>
                                  <div className="bg-white rounded-lg p-3 text-center border">
                                    <p className="text-lg font-bold text-blue-600">{op.tempos?.taxa_ocupacao || 0}%</p>
                                    <p className="text-[10px] text-stone-500 font-medium">Taxa Ocupação</p>
                                  </div>
                                  <div className="bg-white rounded-lg p-3 text-center border">
                                    <p className="text-lg font-bold text-stone-700">{formatarMinutos(op.tempos?.disponivel_min || 0)}</p>
                                    <p className="text-[10px] text-stone-500 font-medium">Disponível</p>
                                  </div>
                                </div>
                              </div>

                              {/* Resumo de tempos — Fora do Horário */}
                              {(op.tempos?.fora_horario_total_min || 0) > 0 && (
                                <div className="px-4 pb-3">
                                  <p className="text-[10px] font-bold text-red-400 uppercase tracking-wider mb-2">Fora do Horário Laboral</p>
                                  <div className="grid grid-cols-3 gap-2">
                                    <div className="bg-red-50 rounded-lg p-3 text-center border border-red-200">
                                      <p className="text-lg font-bold text-red-600">{formatarMinutos(op.tempos?.em_operacoes_fora_hr_min || 0)}</p>
                                      <p className="text-[10px] text-red-500 font-medium">Em Operações</p>
                                    </div>
                                    <div className="bg-red-50 rounded-lg p-3 text-center border border-red-200">
                                      <p className="text-lg font-bold text-red-500">{formatarMinutos(op.tempos?.tempos_mortos_fora_hr_min || 0)}</p>
                                      <p className="text-[10px] text-red-500 font-medium">Tempos Mortos</p>
                                    </div>
                                    <div className="bg-red-50 rounded-lg p-3 text-center border border-red-200">
                                      <p className="text-lg font-bold text-red-700">{formatarMinutos(op.tempos?.fora_horario_total_min || 0)}</p>
                                      <p className="text-[10px] text-red-500 font-medium">Total Fora HR</p>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* ── Sub-abas: Operações Executadas | Pausas ── */}
                              {(() => {
                                const subTab = diaSubTab[idx] || 'operacoes';
                                const setSub = (v) => setDiaSubTab(prev => ({ ...prev, [idx]: v }));
                                const numPausas = (op.gaps?.length || 0) + (op.pausas?.length || 0);
                                return (
                                  <div className="px-4 pt-2">
                                    <div className="flex gap-2 border-b border-stone-200">
                                      <button
                                        type="button"
                                        onClick={() => setSub('operacoes')}
                                        className={`px-3 py-2 text-xs font-semibold transition border-b-2 ${subTab === 'operacoes' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-stone-500 hover:text-stone-700'}`}
                                        data-testid={`subtab-operacoes-${idx}`}
                                      >
                                        Operações Executadas ({op.operacoes?.length || 0})
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setSub('pausas')}
                                        className={`px-3 py-2 text-xs font-semibold transition border-b-2 ${subTab === 'pausas' ? 'border-orange-600 text-orange-700' : 'border-transparent text-stone-500 hover:text-stone-700'}`}
                                        data-testid={`subtab-pausas-${idx}`}
                                      >
                                        Pausas & Tempos Mortos ({numPausas})
                                      </button>
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Gaps (tempos mortos entre operações) */}
                              {(diaSubTab[idx] || 'operacoes') === 'pausas' && op.gaps && op.gaps.length > 0 && (
                                <div className="px-4 pb-3">
                                  <p className="text-xs font-semibold text-amber-700 mb-2 flex items-center gap-1">
                                    <AlertTriangle className="h-3 w-3" /> Tempos Mortos ({op.gaps.length})
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    {op.gaps.map((gap, gIdx) => (
                                      <div key={`gap-${gap.de}-${gap.ate}-${gIdx}`} className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 text-xs">
                                        <span className="font-mono font-bold text-amber-700">{gap.de} → {gap.ate}</span>
                                        <span className="text-amber-600 font-bold ml-2">{formatarMinutos(gap.duracao_minutos)}</span>
                                        <span className="text-amber-500 ml-1 text-[10px]">{gap.entre}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Pausas detalhadas do operador */}
                              {(diaSubTab[idx] || 'operacoes') === 'pausas' && op.pausas && op.pausas.length > 0 && (
                                <div className="px-4 pb-3">
                                  {(() => {
                                    const congeladas = op.pausas.filter(p => (p.motivo || '').toLowerCase().includes('congelad'));
                                    const restantes = op.pausas.filter(p => !((p.motivo || '').toLowerCase().includes('congelad')));
                                    return (
                                      <>
                                        {restantes.length > 0 && (
                                          <>
                                            <p className="text-xs font-semibold text-orange-600 mb-2 flex items-center gap-1">
                                              <PauseCircle className="h-3 w-3" /> Pausas ({restantes.length})
                                            </p>
                                            <div className="flex flex-wrap gap-2 mb-3">
                                              {restantes.map((p, pIdx) => (
                                                <div key={`pausa-${p.inicio}-${p.fim}-${pIdx}`} className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-1.5 text-xs">
                                                  <span className="font-mono font-bold text-orange-700">{p.inicio} → {p.fim}</span>
                                                  <span className="text-orange-600 font-bold ml-2">{formatarMinutos(p.duracao_minutos)}</span>
                                                  <span className="text-orange-500 ml-1 text-[10px]">{p.motivo}</span>
                                                </div>
                                              ))}
                                            </div>
                                          </>
                                        )}
                                        {congeladas.length > 0 && (
                                          <>
                                            <p className="text-xs font-semibold text-cyan-700 mb-2 flex items-center gap-1">
                                              <Snowflake className="h-3 w-3" /> Congelamentos ({congeladas.length})
                                              <span className="text-[9px] text-cyan-500 ml-1 italic font-normal">não contam como tempo morto</span>
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                              {congeladas.map((p, pIdx) => (
                                                <div key={`cong-${p.inicio}-${p.fim}-${pIdx}`} className="bg-cyan-50 border border-cyan-200 rounded-lg px-3 py-1.5 text-xs">
                                                  <span className="font-mono font-bold text-cyan-700">{p.inicio} → {p.fim}</span>
                                                  <span className="text-cyan-600 font-bold ml-2">{formatarMinutos(p.duracao_minutos)}</span>
                                                  <span className="text-cyan-500 ml-1 text-[10px]">{p.motivo}</span>
                                                </div>
                                              ))}
                                            </div>
                                          </>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              )}

                              {/* Tabela de operações */}
                              {(diaSubTab[idx] || 'operacoes') === 'operacoes' && (
                                <OperacoesExecutadasTable
                                  operacoes={op.operacoes || []}
                                  formatarMinutos={formatarMinutos}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Card>

                {/* === Sub-abas globais: Operações Executadas | Pausas === */}
                <Card className="p-0 overflow-hidden">
                  <div className="flex border-b border-stone-200 bg-stone-50">
                    <button
                      type="button"
                      onClick={() => setDiaGlobalTab('operacoes')}
                      className={`flex-1 px-4 py-3 text-sm font-semibold transition flex items-center justify-center gap-2 ${diaGlobalTab === 'operacoes' ? 'bg-white text-emerald-700 border-b-2 border-emerald-600' : 'text-stone-500 hover:text-stone-700'}`}
                      data-testid="dia-global-operacoes-tab"
                    >
                      <Layers className="h-4 w-4" />
                      Operações Executadas ({relatorioDia.operacoes.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setDiaGlobalTab('pausas')}
                      className={`flex-1 px-4 py-3 text-sm font-semibold transition flex items-center justify-center gap-2 ${diaGlobalTab === 'pausas' ? 'bg-white text-orange-700 border-b-2 border-orange-600' : 'text-stone-500 hover:text-stone-700'}`}
                      data-testid="dia-global-pausas-tab"
                    >
                      <PauseCircle className="h-4 w-4 text-orange-500" />
                      Pausas ({relatorioDia.pausas.length})
                    </button>
                  </div>

                  {/* Conteúdo Operações */}
                  {diaGlobalTab === 'operacoes' && (
                    <div className="p-5">
                      {relatorioDia.operacoes.length === 0 ? (
                        <p className="text-stone-400 text-sm text-center py-6">Sem operações neste dia</p>
                      ) : (
                        <OperacoesGlobaisTable
                          operacoes={relatorioDia.operacoes}
                          formatarMinutos={formatarMinutos}
                          onSortedChange={setOperacoesGlobaisSorted}
                        />
                      )}
                    </div>
                  )}

                  {/* Conteúdo Pausas */}
                  {diaGlobalTab === 'pausas' && (
                    <div className="p-5">
                      {relatorioDia.pausas.length === 0 ? (
                        <p className="text-stone-400 text-sm text-center py-6">Sem pausas registadas neste dia</p>
                      ) : (
                        <PausasGlobaisTable
                          pausas={relatorioDia.pausas}
                          formatarMinutos={formatarMinutos}
                          onSortedChange={setPausasGlobaisSorted}
                        />
                      )}
                    </div>
                  )}
                </Card>
              </>
            )}

            {!relatorioDia && !loading && (
              <Card className="p-12 text-center">
                <Calendar className="h-12 w-12 mx-auto text-stone-300 mb-3" />
                <p className="text-stone-500">Selecione uma data e clique "Gerar Relatório"</p>
              </Card>
            )}
          </div>
        )}

        {/* TAB: POR OPERADOR */}
        {activeTab === 'operador' && (
          <div className="space-y-6">

            {/* Relatório do Operador */}
            {relatorioOperador && (
              <div className="space-y-4">
                {/* Cabeçalho */}
                <Card className="p-6 bg-gradient-to-r from-navy to-navy-light text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold">{relatorioOperador.operador.nome}</h2>
                      <p className="opacity-80">
                        {relatorioOperador.periodo_label || relatorioOperador.data}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm opacity-80">Horário de Trabalho</p>
                      <p className="font-semibold">{relatorioOperador.horario_trabalho.inicio} - {relatorioOperador.horario_trabalho.fim}</p>
                    </div>
                  </div>
                </Card>

                {/* Resumo do Dia */}
                {relatorioOperador.resumo_dia.multi_dia && (
                  <div className="text-xs text-stone-500 bg-blue-50 rounded-lg px-3 py-1.5 flex items-center gap-2 mb-1">
                    <AlertCircle className="h-3.5 w-3.5 text-blue-500" />
                    <span>Valores são <strong>médias diárias</strong> ({relatorioOperador.resumo_dia.num_dias} dias com registos)</span>
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                  {!relatorioOperador.resumo_dia.multi_dia && (
                    <Card className="p-4 text-center">
                      <PlayCircle className="h-6 w-6 mx-auto text-green-600 mb-2" />
                      <p className="text-xl font-bold">{relatorioOperador.resumo_dia.hora_entrada || '-'}</p>
                      <p className="text-xs text-stone-600">Entrada</p>
                    </Card>
                  )}
                  {!relatorioOperador.resumo_dia.multi_dia && (
                    <Card className="p-4 text-center">
                      <StopCircle className="h-6 w-6 mx-auto text-red-600 mb-2" />
                      <p className="text-xl font-bold">{relatorioOperador.resumo_dia.hora_saida || '-'}</p>
                      <p className="text-xs text-stone-600">Saída</p>
                    </Card>
                  )}
                  <Card className="p-4 text-center" title="Total = Em Operações + Tarefas Pontuais">
                    <Timer className="h-6 w-6 mx-auto text-blue-600 mb-2" />
                    {(() => {
                      const opsMin = (relatorioOperador.resumo_dia.tempo_em_operacoes_horas || 0) * 60;
                      const pontMin = relatorioOperador.resumo_dia.tempo_pontuais_min || 0;
                      const totalMin = opsMin + pontMin;
                      const hasPontuais = pontMin > 0;
                      return (
                        <>
                          <p className="text-xl font-bold" data-testid="tempo-total-trabalhado">{formatarMinutos(totalMin)}</p>
                          <p className="text-xs text-stone-600">{relatorioOperador.resumo_dia.multi_dia ? 'Tempo Total /dia' : 'Tempo Total Trabalhado'}</p>
                          <div className="flex flex-col gap-0.5 mt-1.5">
                            <p className="text-[10px] text-blue-600 font-medium">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-500 mr-1 align-middle" />
                              {formatarMinutos(opsMin)} em ops
                            </p>
                            {hasPontuais && (
                              <p className="text-[10px] text-purple-600 font-medium">
                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-purple-500 mr-1 align-middle" />
                                {formatarMinutos(pontMin)} pontuais
                              </p>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </Card>
                  <Card className="p-4 text-center">
                    <ClipboardList className="h-6 w-6 mx-auto text-purple-600 mb-2" />
                    <p className="text-xl font-bold">{relatorioOperador.resumo_dia.total_pontuais || 0}</p>
                    <p className="text-xs text-stone-600">Tarefas Pontuais</p>
                    {(relatorioOperador.resumo_dia.total_pecas_pontuais || 0) > 0 && (
                      <p className="text-[10px] text-stone-500 mt-0.5">{relatorioOperador.resumo_dia.total_pecas_pontuais} peças</p>
                    )}
                  </Card>
                  {/* Pausas Legais — almoço + pausas programadas (automáticas) */}
                  <Card className="p-4 text-center" title="Pausa de almoço (cap 90 min) + pausas legais programadas (10+10 min)">
                    <Coffee className="h-6 w-6 mx-auto text-emerald-600 mb-2" />
                    <p className="text-xl font-bold">{formatarMinutos(relatorioOperador.resumo_dia.tempo_pausas_legais_min || 0)}</p>
                    <p className="text-xs text-stone-600">{relatorioOperador.resumo_dia.multi_dia ? 'Pausas Legais /dia' : 'Pausas Legais'}</p>
                  </Card>
                  {/* Outras Pausas — manuais registadas pelo operador (ex.: casa de banho, problema técnico) */}
                  <Card className="p-4 text-center" title="Pausas manuais não legais registadas pelo operador (ex.: casa de banho, problema técnico, aguardar material). Congelamentos não contam aqui.">
                    <Coffee className="h-6 w-6 mx-auto text-amber-600 mb-2" />
                    <p className="text-xl font-bold">{formatarMinutos(relatorioOperador.resumo_dia.tempo_pausas_outras_min || 0)}</p>
                    <p className="text-xs text-stone-600">{relatorioOperador.resumo_dia.multi_dia ? 'Outras Pausas /dia' : 'Outras Pausas'}</p>
                  </Card>
                  {(relatorioOperador.resumo_dia.tempo_congelamentos_min || 0) > 0 && (
                    <Card className="p-4 text-center" title="Tempo em que operações estiveram congeladas. Não conta como tempo morto — operador pode estar a trabalhar noutra operação.">
                      <Snowflake className="h-6 w-6 mx-auto text-cyan-600 mb-2" />
                      <p className="text-xl font-bold text-cyan-700">{formatarMinutos(relatorioOperador.resumo_dia.tempo_congelamentos_min || 0)}</p>
                      <p className="text-xs text-stone-600">{relatorioOperador.resumo_dia.multi_dia ? 'Congelamentos /dia' : 'Congelamentos'}</p>
                      <p className="text-[9px] text-cyan-500 mt-0.5 italic">não é tempo morto</p>
                    </Card>
                  )}
                  <Card className="p-4 text-center" title="Tempo entre operações (já descontado almoço automático)">
                    <AlertCircle className="h-6 w-6 mx-auto text-orange-600 mb-2" />
                    <p className="text-xl font-bold">{formatarMinutos(relatorioOperador.resumo_dia.tempo_entre_operacoes_min)}</p>
                    <p className="text-xs text-stone-600">{relatorioOperador.resumo_dia.multi_dia ? 'Entre Op. /dia' : 'Entre Operações'}</p>
                  </Card>
                  <Card className="p-4 text-center">
                    <Clock className="h-6 w-6 mx-auto text-purple-600 mb-2" />
                    <p className="text-xl font-bold">{formatarMinutos(relatorioOperador.resumo_dia.tempo_fora_horario_min)}</p>
                    <p className="text-xs text-stone-600">{relatorioOperador.resumo_dia.multi_dia ? 'Fora Hr. /dia' : 'Fora Horário'}</p>
                  </Card>
                </div>

                {/* Taxa de Ocupação e Eficiência */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="p-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Percent className="h-5 w-5 text-navy" />
                      Taxa de Ocupação
                    </h3>
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <div className="h-4 bg-stone-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
                            style={{ width: `${Math.min(relatorioOperador.resumo_dia.taxa_ocupacao_percentual, 100)}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-2xl font-bold text-navy">
                        {relatorioOperador.resumo_dia.taxa_ocupacao_percentual}%
                      </span>
                    </div>
                    <p className="text-sm text-stone-600 mt-2">
                      {relatorioOperador.resumo_dia.multi_dia 
                        ? `Média diária (${relatorioOperador.resumo_dia.num_dias} dias)`
                        : 'Tempo em operações vs tempo activo (ops + entre ops)'}
                    </p>
                  </Card>

                  <Card className="p-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Target className="h-5 w-5 text-navy" />
                      Eficiência Média
                    </h3>
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <div className="h-4 bg-stone-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${
                              relatorioOperador.resumo_dia.eficiencia_media >= 100 ? 'bg-gradient-to-r from-green-500 to-blue-500' :
                              relatorioOperador.resumo_dia.eficiencia_media >= 90 ? 'bg-green-500' :
                              relatorioOperador.resumo_dia.eficiencia_media >= 76 ? 'bg-yellow-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(relatorioOperador.resumo_dia.eficiencia_media, 150)}%` }}
                          />
                        </div>
                      </div>
                      <span className={`text-2xl font-bold ${
                        relatorioOperador.resumo_dia.eficiencia_media >= 100 ? 'text-blue-600' :
                        relatorioOperador.resumo_dia.eficiencia_media >= 90 ? 'text-green-600' :
                        relatorioOperador.resumo_dia.eficiencia_media >= 76 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {relatorioOperador.resumo_dia.eficiencia_media}%
                      </span>
                    </div>
                    <p className="text-sm text-stone-600 mt-2">
                      {relatorioOperador.resumo_dia.pecas_unicas || relatorioOperador.resumo_dia.total_pecas_produzidas} peças (lote) em {relatorioOperador.resumo_dia.total_operacoes} operações
                    </p>
                  </Card>
                </div>

                {/* Operações do Dia */}
                <Card className="p-3">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-navy" />
                    Operações do Dia ({relatorioOperador.operacoes.length})
                  </h3>
                  <div>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-[11px] text-stone-600 border-b bg-stone-50">
                          <th className="py-1.5 px-1 font-semibold">Data</th>
                          <th className="py-1.5 px-1 font-semibold">Operação</th>
                          <th className="py-1.5 px-1 font-semibold">OF</th>
                          <th className="py-1.5 px-1 font-semibold">Cliente</th>
                          <th className="py-1.5 px-1 font-semibold">Lote</th>
                          <th className="py-1.5 px-1 font-semibold">Modelo</th>
                          <th className="py-1.5 px-1 font-semibold">Início</th>
                          <th className="py-1.5 px-1 font-semibold">Fim</th>
                          <th className="py-1.5 px-1 text-right font-semibold">Duração</th>
                          <th className="py-1.5 px-1 text-right font-semibold">Pcs</th>
                          <th className="py-1.5 px-1 font-semibold">Entre Tar.</th>
                          <th className="py-1.5 px-1 font-semibold text-center">Tipo</th>
                          <th className="py-1.5 px-1 text-right font-semibold">T/Pç</th>
                          <th className="py-1.5 px-1 text-right font-semibold">T.Ref</th>
                          <th className="py-1.5 px-1 text-right font-semibold">Efic.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          // Lista de janelas de pausas legais (em minutos do dia) para descontar do gap "Entre Tar."
                          // Inclui Almoço + pausas adicionais (manhã, tarde, etc).
                          const ht = relatorioOperador.horario_trabalho || {};
                          const hmToMin = (s) => { try { const [h, m] = (s || '').split(':').map(Number); return h * 60 + m; } catch { return 0; } };
                          const janelasLegais = [];
                          if (ht.almoco) {
                            const [ini, fim] = ht.almoco.split('-').map(x => x.trim());
                            if (ini && fim) janelasLegais.push([hmToMin(ini), hmToMin(fim)]);
                          }
                          (ht.pausas_programadas || []).forEach(p => {
                            const a = hmToMin(p.inicio), b = hmToMin(p.fim);
                            if (b > a) janelasLegais.push([a, b]);
                          });
                          // Calcula a interseção em segundos de [aSeg, bSeg] com qualquer janela legal
                          const overlapPausasLegaisSec = (prevFimStr, curIniStr) => {
                            const toMin = (t) => { const [h, m, s] = (t.length >= 8 ? t : t + ':00').split(':').map(Number); return h * 60 + m + (s || 0) / 60; };
                            const a = toMin(prevFimStr), b = toMin(curIniStr);
                            if (b <= a) return 0;
                            let total = 0;
                            for (const [x, y] of janelasLegais) {
                              total += Math.max(0, Math.min(b, y) - Math.max(a, x));
                            }
                            return Math.round(total * 60);
                          };
                          return relatorioOperador.operacoes.map((op, idx) => {
                          // Calcular tempo entre tarefas
                          // Para ops normais: gap até op normal anterior
                          // Para ops pontuais: gap até pontual anterior
                          let entreTarefas = null;
                          if (idx > 0) {
                            // Procurar operação anterior de QUALQUER tipo (regular ou pontual),
                            // no MESMO dia, com fim válido. Para uma tarefa pontual, "Entre Tar."
                            // significa o tempo desde a última actividade (não a última pontual
                            // do dia, que pode estar a horas distantes).
                            let prev = null;
                            const dataAtual = op.data;
                            for (let i = idx - 1; i >= 0; i--) {
                              const candidate = relatorioOperador.operacoes[i];
                              if (candidate.data !== dataAtual) break; // dia diferente
                              if (candidate.hora_fim && candidate.hora_fim !== 'Em curso') {
                                prev = candidate;
                                break;
                              }
                            }
                            if (prev) {
                              const prevFim = prev.hora_fim;
                              const curInicio = op.hora_inicio;
                              if (prevFim && curInicio && prevFim !== 'Em curso') {
                                const parseParts = (t) => t.split(':').map(Number);
                                const [ph, pm, ps] = parseParts(prevFim.length >= 8 ? prevFim : prevFim + ':00');
                                const [ch, cm, cs] = parseParts(curInicio.length >= 8 ? curInicio : curInicio + ':00');
                                let diffSec = ((ch * 3600 + cm * 60 + (cs || 0)) - (ph * 3600 + pm * 60 + (ps || 0)));
                                // Descontar pausas legais (Almoço, pausa manhã, pausa tarde, ...)
                                diffSec = Math.max(0, diffSec - overlapPausasLegaisSec(prevFim, curInicio));
                                if (diffSec > 0) {
                                  const m = Math.floor(diffSec / 60);
                                  const s = diffSec % 60;
                                  entreTarefas = m > 0 ? `${m}m ${s}s` : `${s}s`;
                                }
                              }
                            }
                          }
                          const fmtTime = (t) => {
                            if (!t || t === 'Em curso') return t || '-';
                            return t.length >= 8 ? t : t + ':00';
                          };
                          return (
                          <tr key={op.id || op.operacao_id || `op-${idx}`} className="border-b last:border-0 hover:bg-stone-50">
                            <td className="py-1.5 px-1 text-stone-500 whitespace-nowrap">
                              {op.data || '-'}
                            </td>
                            <td className="py-1.5 px-1">
                              <div className="flex items-center gap-1">
                                <span className="font-mono text-[10px] bg-blue-600 text-white px-1.5 py-0.5 rounded font-bold">{op.operacao_codigo || '-'}</span>
                                <span className="truncate max-w-[120px]" title={op.operacao_nome}>{op.operacao_nome}</span>
                              </div>
                            </td>
                            <td className="py-1.5 px-1 font-medium whitespace-nowrap">
                              {op.of_numero || '-'}
                            </td>
                            <td className="py-1.5 px-1 text-stone-600 truncate max-w-[80px]" title={op.cliente}>
                              {op.cliente || '-'}
                            </td>
                            <td className="py-1.5 px-1 font-medium text-amber-700 whitespace-nowrap">
                              {op.lote_codigo || '-'}
                            </td>
                            <td className="py-1.5 px-1 text-stone-600 truncate max-w-[90px]" title={op.artigo}>
                              {op.artigo || '-'}
                            </td>
                            <td className="py-1.5 px-1 font-mono text-[10px] whitespace-nowrap">{fmtTime(op.hora_inicio)}</td>
                            <td className="py-1.5 px-1 font-mono text-[10px] whitespace-nowrap">{fmtTime(op.hora_fim)}</td>
                            <td className="py-1.5 px-1 text-right whitespace-nowrap">{formatarMinutos(op.duracao_minutos)}</td>
                            <td className="py-1.5 px-1 text-right font-semibold">{op.quantidade_produzida}</td>
                            <td className="py-1.5 px-1">
                              {entreTarefas ? (
                                <span className="text-[10px] px-1 py-0.5 rounded inline-block bg-amber-50 text-amber-700 font-medium">
                                  {entreTarefas}
                                </span>
                              ) : (
                                <span className="text-stone-300">-</span>
                              )}
                            </td>
                            <td className="py-1.5 px-1 text-center whitespace-nowrap">
                              {op.tipo_conclusao === 'parcial' ? (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-orange-100 text-orange-700" data-testid="badge-parcial">Parcial</span>
                              ) : op.tipo_conclusao === 'concluido' ? (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-700" data-testid="badge-concluido">Concluído</span>
                              ) : op.tipo_conclusao === 'congelado' ? (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-cyan-100 text-cyan-700" data-testid="badge-congelado">Congelado</span>
                              ) : op.tipo_conclusao === 'cancelado' ? (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-600" data-testid="badge-cancelado">Cancelado</span>
                              ) : op.tipo_conclusao === 'pontual' ? (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700" data-testid="badge-pontual">Pontual</span>
                              ) : (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-stone-100 text-stone-500" data-testid="badge-normal">Normal</span>
                              )}
                            </td>
                            <td className="py-1.5 px-1 text-right text-stone-600 whitespace-nowrap">
                              {op.quantidade_produzida > 0 ? (
                                <span>
                                  {op.tempo_medio_peca_seg >= 60 
                                    ? `${Math.floor(op.tempo_medio_peca_seg / 60)}m ${Math.round(op.tempo_medio_peca_seg % 60)}s`
                                    : `${Math.round(op.tempo_medio_peca_seg)}s`
                                  }
                                </span>
                              ) : '-'}
                            </td>
                            <td className="py-1.5 px-1 text-right text-stone-500 whitespace-nowrap">
                              {op.tempo_referencia_seg > 0 ? (
                                op.tempo_referencia_seg >= 60 
                                  ? `${Math.floor(op.tempo_referencia_seg / 60)}m ${Math.round(op.tempo_referencia_seg % 60)}s`
                                  : `${Math.round(op.tempo_referencia_seg)}s`
                              ) : '-'}
                            </td>
                            <td className={`py-1.5 px-1 text-right font-bold whitespace-nowrap ${
                              op.tipo_conclusao === 'pontual' ? 'text-stone-300' :
                              Math.min(op.eficiencia, 300) >= 100 ? 'text-blue-600' :
                              op.eficiencia >= 90 ? 'text-green-600' :
                              op.eficiencia >= 76 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {op.tipo_conclusao === 'pontual' ? '—' : `${Math.min(op.eficiencia, 300)}%`}
                            </td>
                          </tr>
                          );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                </Card>

                {/* Pausas */}
                {relatorioOperador.pausas && relatorioOperador.pausas.length > 0 && (() => {
                  // Detectar multi-dia: usa intervalo data_inicio/data_fim
                  const isMultiDia = dataInicio && dataFim && dataInicio !== dataFim;
                  if (isMultiDia) {
                    // Agregar pausas por motivo somando durações e contando ocorrências
                    const agrupadas = new Map();
                    for (const p of relatorioOperador.pausas) {
                      const k = p.motivo || 'Sem motivo';
                      const prev = agrupadas.get(k) || { motivo: k, ocorrencias: 0, total_min: 0 };
                      prev.ocorrencias += 1;
                      prev.total_min += (p.duracao_minutos || 0);
                      agrupadas.set(k, prev);
                    }
                    const linhas = Array.from(agrupadas.values()).sort((a, b) => b.total_min - a.total_min);
                    return (
                      <Card className="p-6">
                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                          <Coffee className="h-5 w-5 text-navy" />
                          Pausas no Período ({linhas.length} {linhas.length === 1 ? 'tipo' : 'tipos'} · {relatorioOperador.pausas.length} ocorrências)
                          <span className="text-xs text-stone-500 ml-2 font-normal">— agregado por motivo</span>
                        </h3>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="text-left text-sm text-stone-600 border-b">
                                <th className="pb-2">Motivo</th>
                                <th className="pb-2 text-center">Ocorrências</th>
                                <th className="pb-2 text-right">Duração Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {linhas.map((l, idx) => (
                                <tr key={`pg-${idx}`} className="border-b last:border-0">
                                  <td className="py-3 flex items-center gap-2">
                                    <Coffee className="h-4 w-4 text-amber-600" />
                                    {l.motivo}
                                  </td>
                                  <td className="py-3 text-center">{l.ocorrencias}</td>
                                  <td className="py-3 text-right font-semibold">{formatarMinutos(l.total_min)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </Card>
                    );
                  }
                  return (
                  <Card className="p-6">
                    <h3 className="font-semibold mb-4 flex items-center gap-2">
                      <Coffee className="h-5 w-5 text-navy" />
                      Pausas Registadas ({relatorioOperador.pausas.length})
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left text-sm text-stone-600 border-b">
                            <th className="pb-2">Motivo</th>
                            <th className="pb-2">Início</th>
                            <th className="pb-2">Fim</th>
                            <th className="pb-2 text-right">Duração</th>
                          </tr>
                        </thead>
                        <tbody>
                          {relatorioOperador.pausas.map((pausa, idx) => (
                            <tr key={pausa.id || `pausa-${idx}`} className="border-b last:border-0">
                              <td className="py-3 flex items-center gap-2">
                                <Coffee className="h-4 w-4 text-amber-600" />
                                {pausa.motivo}
                              </td>
                              <td className="py-3">{pausa.hora_inicio || '-'}</td>
                              <td className="py-3">{pausa.hora_fim || '-'}</td>
                              <td className="py-3 text-right font-semibold">{formatarMinutos(pausa.duracao_minutos)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {/* TAB: POR OF */}
        {activeTab === 'of' && (
          <div className="space-y-6">
            {/* Seleção Melhorada de OF */}
            <Card className="p-6">
              <div className="space-y-4">
                {/* Cabeçalho */}
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-stone-700 flex items-center gap-2">
                    <Package className="h-5 w-5 text-sage" />
                    Selecionar Ordem de Fabrico
                  </h3>
                </div>

                {/* Filtros */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  {/* Pesquisa */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                    <input
                      type="text"
                      placeholder="Pesquisar OF..."
                      value={ofSearchTerm}
                      onChange={(e) => setOfSearchTerm(e.target.value)}
                      className="w-full h-10 pl-10 pr-3 border rounded-lg bg-white text-sm"
                    />
                  </div>
                  
                  {/* Filtro Estado */}
                  <div className="relative">
                    <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                    <select
                      value={ofFiltroEstado}
                      onChange={(e) => setOfFiltroEstado(e.target.value)}
                      className="w-full h-10 pl-10 pr-3 border rounded-lg bg-white text-sm appearance-none"
                    >
                      <option value="">Todos os Estados</option>
                      <option value="em_curso">Em Curso</option>
                      <option value="terminado">Terminada</option>
                      <option value="rascunho">Rascunho</option>
                      <option value="aguardar_entrada">Aguardar Entrada</option>
                    </select>
                  </div>
                  
                  {/* Filtro Marca/Cliente */}
                  <div>
                    <select
                      value={ofFiltroMarca}
                      onChange={(e) => setOfFiltroMarca(e.target.value)}
                      className="w-full h-10 px-3 border rounded-lg bg-white text-sm"
                    >
                      <option value="">Todas as Marcas</option>
                      {[...new Set(ofs.map(of => of.cliente).filter(Boolean))].sort().map(marca => (
                        <option key={marca} value={marca}>{marca}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Ordenação */}
                  <div className="relative">
                    <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                    <select
                      value={ofOrdenacao}
                      onChange={(e) => setOfOrdenacao(e.target.value)}
                      className="w-full h-10 pl-10 pr-3 border rounded-lg bg-white text-sm appearance-none"
                    >
                      <option value="recentes">Mais Recentes</option>
                      <option value="antigas">Mais Antigas</option>
                      <option value="estado">Por Estado</option>
                      <option value="quantidade_desc">Maior Quantidade</option>
                      <option value="quantidade_asc">Menor Quantidade</option>
                    </select>
                  </div>
                </div>

                {/* Seletor de OF + Botão Gerar */}
                <div className="flex gap-3 items-end">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-stone-600 mb-1.5">
                      Ordem de Fabrico
                      <span className="text-stone-400 font-normal ml-2">
                        ({(() => {
                          const filtradas = ofs.filter(of => {
                            if (ofSearchTerm) {
                              const termo = ofSearchTerm.toLowerCase();
                              const match = (of.numero_of || of.ref || '').toLowerCase().includes(termo) ||
                                            (of.artigo || '').toLowerCase().includes(termo) ||
                                            (of.cliente || '').toLowerCase().includes(termo);
                              if (!match) return false;
                            }
                            if (ofFiltroEstado && of.status !== ofFiltroEstado) return false;
                            if (ofFiltroMarca && of.cliente !== ofFiltroMarca) return false;
                            return true;
                          });
                          return filtradas.length;
                        })()} resultados)
                      </span>
                    </label>
                    <select
                      value={selectedOf}
                      onChange={(e) => setSelectedOf(e.target.value)}
                      className="w-full h-12 px-4 border-2 rounded-xl bg-white text-base font-medium focus:border-sage focus:ring-2 focus:ring-sage/20 transition-all"
                    >
                      <option value="">-- Selecione uma OF --</option>
                      {(() => {
                        // Filtrar OFs
                        let ofsFiltradas = ofs.filter(of => {
                          if (ofSearchTerm) {
                            const termo = ofSearchTerm.toLowerCase();
                            const matchNumero = (of.numero_of || of.ref || '').toLowerCase().includes(termo);
                            const matchArtigo = (of.artigo || '').toLowerCase().includes(termo);
                            const matchCliente = (of.cliente || '').toLowerCase().includes(termo);
                            if (!matchNumero && !matchArtigo && !matchCliente) return false;
                          }
                          if (ofFiltroEstado && of.status !== ofFiltroEstado) return false;
                          if (ofFiltroMarca && of.cliente !== ofFiltroMarca) return false;
                          return true;
                        });

                        // Ordenar
                        ofsFiltradas = [...ofsFiltradas].sort((a, b) => {
                          switch (ofOrdenacao) {
                            case 'recentes':
                              return new Date(b.criado_em || 0) - new Date(a.criado_em || 0);
                            case 'antigas':
                              return new Date(a.criado_em || 0) - new Date(b.criado_em || 0);
                            case 'estado':
                              const ordem = { em_curso: 0, aguardar_entrada: 1, rascunho: 2, terminado: 3 };
                              return (ordem[a.status] || 99) - (ordem[b.status] || 99);
                            case 'quantidade_desc':
                              return (b.quantidade_total || 0) - (a.quantidade_total || 0);
                            case 'quantidade_asc':
                              return (a.quantidade_total || 0) - (b.quantidade_total || 0);
                            default:
                              return 0;
                          }
                        });

                        // Mapeamento de estados para labels
                        const estadoLabels = {
                          em_curso: '🟢 Em Curso',
                          terminado: '✅ Terminada',
                          rascunho: '⚪ Rascunho',
                          aguardar_entrada: '🟡 Aguardar'
                        };

                        return ofsFiltradas.map(of => (
                          <option key={of.id} value={of.id}>
                            {of.numero_of || of.ref} - {of.artigo || 'Sem artigo'} • {of.cliente || 'Sem marca'} • {of.quantidade_total || 0} un • {estadoLabels[of.status] || of.status || 'Sem estado'}
                          </option>
                        ));
                      })()}
                    </select>
                  </div>
                  
                  <Button 
                    onClick={loadRelatorioOf} 
                    disabled={loading || !selectedOf}
                    className="h-12 px-6"
                    data-testid="gerar-relatorio-of"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Gerar Relatório
                  </Button>
                  {relatorioOf && (
                    <>
                      <Button variant="outline" onClick={exportCSV} disabled={exporting} className="h-12" data-testid="export-csv-of">
                        <Download className="h-4 w-4 mr-1" /> CSV
                      </Button>
                      <Button variant="outline" onClick={exportPDF} disabled={exporting} className="h-12" data-testid="export-pdf-of">
                        <FileText className="h-4 w-4 mr-1" /> PDF
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </Card>

            {/* Relatório da OF */}
            {relatorioOf && (
              <div className="space-y-4">
                {/* Cabeçalho */}
                <Card className="p-6 bg-gradient-to-r from-sage to-sage-dark text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold">OF {relatorioOf.of.numero}</h2>
                      <p className="opacity-80">{relatorioOf.of.cliente} - {relatorioOf.of.artigo}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm opacity-80">Quantidade Total</p>
                      <p className="text-2xl font-bold">{relatorioOf.of.quantidade_total}</p>
                    </div>
                  </div>
                </Card>

                {/* Resumo */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="p-4 text-center">
                    <Package className="h-6 w-6 mx-auto text-green-600 mb-2" />
                    <p className="text-xl font-bold">{relatorioOf.resumo.total_pecas_produzidas}</p>
                    <p className="text-xs text-stone-600">Peças Produzidas</p>
                  </Card>
                  <Card className="p-4 text-center">
                    <Clock className="h-6 w-6 mx-auto text-blue-600 mb-2" />
                    <p className="text-xl font-bold">{formatarTempo(relatorioOf.resumo.tempo_total_horas)}</p>
                    <p className="text-xs text-stone-600">Tempo Total</p>
                  </Card>
                  <Card className="p-4 text-center">
                    <Target className="h-6 w-6 mx-auto text-amber-600 mb-2" />
                    <p className="text-xl font-bold">{relatorioOf.resumo.eficiencia_media}%</p>
                    <p className="text-xs text-stone-600">Eficiência Média</p>
                  </Card>
                  <Card className="p-4 text-center">
                    <Users className="h-6 w-6 mx-auto text-purple-600 mb-2" />
                    <p className="text-xl font-bold">{relatorioOf.resumo.num_operadores}</p>
                    <p className="text-xs text-stone-600">Operadores</p>
                  </Card>
                </div>

                {/* Por Operador */}
                <Card className="p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Users className="h-5 w-5 text-navy" />
                    Por Operador
                  </h3>
                  <div className="space-y-2">
                    {relatorioOf.por_operador.map((op, idx) => (
                      <div key={op.operador_id || `op-${idx}`} className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-navy">#{idx + 1}</span>
                          <span className="font-medium">{op.nome}</span>
                        </div>
                        <div className="flex items-center gap-6 text-sm">
                          <span><strong>{op.total_pecas}</strong> peças</span>
                          <span><strong>{formatarTempo(op.tempo_total_horas)}</strong></span>
                          <span className={`font-bold ${
                            op.eficiencia_media >= 100 ? 'text-blue-600' :
                            op.eficiencia_media >= 90 ? 'text-green-600' : 'text-yellow-600'
                          }`}>{op.eficiencia_media}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Por Operação */}
                <Card className="p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Briefcase className="h-5 w-5 text-navy" />
                    Por Operação
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-xs text-stone-600 border-b bg-stone-50">
                          <th className="py-2 px-2">Código</th>
                          <th className="py-2 px-2">Operação</th>
                          <th className="py-2 px-2 text-right">Peças</th>
                          <th className="py-2 px-2 text-right">Tempo Total</th>
                          <th className="py-2 px-2 text-right">Tempo/Peça</th>
                          <th className="py-2 px-2 text-right">Tempo Ref.</th>
                          <th className="py-2 px-2 text-right">Eficiência</th>
                          <th className="py-2 px-2 text-right">Operadores</th>
                        </tr>
                      </thead>
                      <tbody>
                        {relatorioOf.por_operacao.map((op, idx) => (
                          <tr key={op.operacao_id || `oplist-${idx}`} className="border-b last:border-0 hover:bg-stone-50">
                            <td className="py-2.5 px-2">
                              <span className="font-mono text-xs bg-blue-600 text-white px-2 py-0.5 rounded font-bold">{op.codigo || '-'}</span>
                            </td>
                            <td className="py-2.5 px-2">{op.nome || '-'}</td>
                            <td className="py-2.5 px-2 text-right font-semibold">{op.total_pecas}</td>
                            <td className="py-2.5 px-2 text-right">{formatarTempo(op.tempo_total_horas)}</td>
                            <td className="py-2.5 px-2 text-right text-stone-600">
                              {op.total_pecas > 0 ? (
                                op.tempo_medio_peca_seg >= 60 
                                  ? `${Math.floor(op.tempo_medio_peca_seg / 60)}m ${Math.round(op.tempo_medio_peca_seg % 60)}s`
                                  : `${Math.round(op.tempo_medio_peca_seg)}s`
                              ) : '-'}
                            </td>
                            <td className="py-2.5 px-2 text-right text-stone-500">
                              {op.tempo_referencia_seg > 0 ? (
                                op.tempo_referencia_seg >= 60 
                                  ? `${Math.floor(op.tempo_referencia_seg / 60)}m ${Math.round(op.tempo_referencia_seg % 60)}s`
                                  : `${Math.round(op.tempo_referencia_seg)}s`
                              ) : '-'}
                            </td>
                            <td className={`py-2.5 px-2 text-right font-bold ${
                              op.eficiencia_media >= 100 ? 'text-blue-600' :
                              op.eficiencia_media >= 90 ? 'text-green-600' : 'text-yellow-600'
                            }`}>{op.eficiencia_media}%</td>
                            <td className="py-2.5 px-2 text-right">{op.num_operadores}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* TAB: POR OPERAÇÃO */}
        {activeTab === 'operacao' && (
          <div className="space-y-6">
            {/* Filtros */}
            <Card className="p-6">
              <div className="flex flex-wrap gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Data Início</label>
                  <input
                    type="date"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                    className="h-10 px-3 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Data Fim</label>
                  <input
                    type="date"
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                    className="h-10 px-3 border rounded-lg"
                  />
                </div>
                <div className="flex-1 min-w-[240px]">
                  <label className="block text-sm font-medium text-stone-700 mb-1">Filtrar por Operação (opcional)</label>
                  <Popover open={operacaoFiltroOpen} onOpenChange={setOperacaoFiltroOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        role="combobox"
                        aria-expanded={operacaoFiltroOpen}
                        data-testid="combo-operacao-filtro"
                        className="w-full h-10 px-3 border rounded-lg bg-white text-left text-sm flex items-center justify-between gap-2 hover:bg-stone-50"
                      >
                        <span className="truncate">
                          {selectedOperacaoFiltro
                            ? (() => {
                                const op = operacoesDisponiveis.find(o => o.id === selectedOperacaoFiltro);
                                return op ? `${op.codigo} — ${op.nome}` : 'Operação selecionada';
                              })()
                            : <span className="text-stone-400">Todas as operações</span>}
                        </span>
                        <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-[380px]" align="start">
                      <Command
                        filter={(value, search) => {
                          // Case-insensitive substring match no valor (que contém código + nome)
                          if (!search) return 1;
                          return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
                        }}
                      >
                        <CommandInput placeholder="Escreva código ou nome da operação..." data-testid="combo-operacao-input" />
                        <CommandList>
                          <CommandEmpty>Sem resultados.</CommandEmpty>
                          <CommandGroup>
                            <CommandItem
                              value="__todas__ todas operações"
                              onSelect={() => { setSelectedOperacaoFiltro(''); setOperacaoFiltroOpen(false); }}
                            >
                              <Check className={`mr-2 h-4 w-4 ${!selectedOperacaoFiltro ? 'opacity-100' : 'opacity-0'}`} />
                              Todas as operações
                            </CommandItem>
                            {operacoesDisponiveis.map(op => (
                              <CommandItem
                                key={op.id}
                                value={`${op.codigo || ''} ${op.nome || ''}`}
                                onSelect={() => { setSelectedOperacaoFiltro(op.id); setOperacaoFiltroOpen(false); }}
                                data-testid={`combo-operacao-item-${op.codigo}`}
                              >
                                <Check className={`mr-2 h-4 w-4 ${selectedOperacaoFiltro === op.id ? 'opacity-100' : 'opacity-0'}`} />
                                <span className="font-mono text-xs bg-stone-100 px-1.5 py-0.5 rounded mr-2">{op.codigo}</span>
                                <span className="truncate">{op.nome}</span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <Button onClick={loadRelatorioOperacoes} disabled={loading}>
                  <FileText className="h-4 w-4 mr-2" />
                  Gerar Relatório
                </Button>
                {relatorioOperacoes && (
                  <>
                    <Button variant="outline" onClick={exportCSV} disabled={exporting} data-testid="export-csv-ops">
                      <Download className="h-4 w-4 mr-1" /> CSV
                    </Button>
                    <Button variant="outline" onClick={exportPDF} disabled={exporting} data-testid="export-pdf-ops">
                      <FileText className="h-4 w-4 mr-1" /> PDF
                    </Button>
                  </>
                )}
              </div>
            </Card>

            {/* Relatório de Operações */}
            {relatorioOperacoes && (
              <div className="space-y-4">
                {/* Resumo Geral */}
                <Card className="p-6 bg-gradient-to-r from-purple-500 to-purple-600 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold">Relatório de Operações</h2>
                      <p className="opacity-80">
                        {relatorioOperacoes.periodo.inicio} a {relatorioOperacoes.periodo.fim}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold">{relatorioOperacoes.total_operacoes}</p>
                      <p className="text-sm opacity-80">operações com actividade</p>
                    </div>
                  </div>
                </Card>

                {/* KPIs */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="p-4 text-center">
                    <Package className="h-6 w-6 mx-auto text-green-600 mb-2" />
                    <p className="text-2xl font-bold">{relatorioOperacoes.total_pecas.toLocaleString()}</p>
                    <p className="text-xs text-stone-600">Total de Peças</p>
                  </Card>
                  <Card className="p-4 text-center">
                    <Clock className="h-6 w-6 mx-auto text-blue-600 mb-2" />
                    <p className="text-2xl font-bold">{formatarTempo(relatorioOperacoes.total_horas)}</p>
                    <p className="text-xs text-stone-600">Tempo Total</p>
                  </Card>
                  <Card className="p-4 text-center">
                    <Timer className="h-6 w-6 mx-auto text-purple-600 mb-2" />
                    <p className="text-2xl font-bold">{relatorioOperacoes.total_operacoes}</p>
                    <p className="text-xs text-stone-600">Operações</p>
                  </Card>
                </div>

                {/* Lista de Operações */}
                <Card className="p-6">
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <Timer className="h-5 w-5 text-navy" />
                    Tempo por Operação e Operador
                  </h3>
                  <div className="space-y-3">
                    {relatorioOperacoes.operacoes.map((op) => (
                      <div 
                        key={op.operacao_id}
                        className="border rounded-xl overflow-hidden"
                      >
                        {/* Cabeçalho da Operação */}
                        <button
                          onClick={() => {
                            setOperacaoExpandida(operacaoExpandida === op.operacao_id ? null : op.operacao_id);
                            setFiltroOperadorDetalhe('');
                          }}
                          className="w-full p-4 bg-stone-50 hover:bg-stone-100 transition-colors flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <span className="font-mono bg-navy text-white px-2 py-1 rounded text-sm">
                              {op.operacao_codigo}
                            </span>
                            <div className="text-left">
                              <p className="font-semibold">{op.operacao_nome}</p>
                              {op.categoria_nome && (
                                <p className="text-xs text-stone-500">{op.categoria_nome}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-right">
                              <p className="font-bold text-navy">{op.total_pecas.toLocaleString()}</p>
                              <p className="text-xs text-stone-500">peças</p>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-blue-600">{formatarTempo(op.total_tempo_horas)}</p>
                              <p className="text-xs text-stone-500">tempo total</p>
                            </div>
                            <div className="text-right">
                              <p className={`font-bold ${
                                op.eficiencia_media >= 100 ? 'text-blue-600' :
                                op.eficiencia_media >= 90 ? 'text-green-600' :
                                op.eficiencia_media >= 76 ? 'text-yellow-600' : 'text-red-600'
                              }`}>{op.eficiencia_media}%</p>
                              <p className="text-xs text-stone-500">eficiência</p>
                            </div>
                            <div className="flex items-center gap-1 text-stone-400">
                              <Users className="h-4 w-4" />
                              <span className="text-sm">{op.num_operadores}</span>
                            </div>
                            {operacaoExpandida === op.operacao_id ? (
                              <ChevronUp className="h-5 w-5 text-stone-400" />
                            ) : (
                              <ChevronDown className="h-5 w-5 text-stone-400" />
                            )}
                          </div>
                        </button>
                        
                        {/* Detalhes Expandidos */}
                        {operacaoExpandida === op.operacao_id && (
                          <div className="p-4 border-t bg-white space-y-6">
                            {/* Resumo por Operador */}
                            <div>
                              <h4 className="text-sm font-semibold text-stone-600 mb-3 flex items-center gap-2">
                                <User className="h-4 w-4" />
                                Resumo por Operador
                              </h4>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-left text-xs text-stone-500 border-b bg-stone-50">
                                      <th className="py-2 px-2">Operador</th>
                                      <th className="py-2 px-2 text-right">Sessões</th>
                                      <th className="py-2 px-2 text-right">Peças</th>
                                      <th className="py-2 px-2 text-right">Tempo</th>
                                      <th className="py-2 px-2 text-right">T.Médio/Peça</th>
                                      <th className="py-2 px-2 text-right">Eficiência</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {op.por_operador.map((operador, idx) => (
                                      <tr 
                                        key={operador.operador_id} 
                                        className={`border-b last:border-0 cursor-pointer transition-colors ${
                                          filtroOperadorDetalhe === operador.operador_id 
                                            ? 'bg-blue-50 ring-1 ring-blue-200' 
                                            : idx === 0 ? 'bg-yellow-50 hover:bg-yellow-100' : 'hover:bg-stone-50'
                                        }`}
                                        onClick={() => setFiltroOperadorDetalhe(
                                          filtroOperadorDetalhe === operador.operador_id ? '' : operador.operador_id
                                        )}
                                        data-testid={`operador-row-${operador.operador_id}`}
                                      >
                                        <td className="py-2 px-2">
                                          <div className="flex items-center gap-2">
                                            {idx === 0 && <Trophy className="h-4 w-4 text-yellow-500" />}
                                            {operador.foto_url ? (
                                              <img src={operador.foto_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                                            ) : (
                                              <div className="w-6 h-6 rounded-full bg-stone-200 flex items-center justify-center text-xs font-bold">
                                                {operador.operador_nome?.charAt(0)?.toUpperCase()}
                                              </div>
                                            )}
                                            <span className="font-medium">{operador.operador_nome}</span>
                                          </div>
                                        </td>
                                        <td className="py-2 px-2 text-right">{operador.num_sessoes}</td>
                                        <td className="py-2 px-2 text-right font-semibold">{operador.total_pecas.toLocaleString()}</td>
                                        <td className="py-2 px-2 text-right">
                                          <span className="font-semibold text-blue-600">{formatarTempo(operador.total_tempo_horas)}</span>
                                        </td>
                                        <td className="py-2 px-2 text-right text-stone-600">
                                          {operador.total_pecas > 0 ? (() => {
                                            const tempoMedioSeg = (operador.total_tempo_horas * 3600) / operador.total_pecas;
                                            return tempoMedioSeg >= 60 
                                              ? `${Math.floor(tempoMedioSeg / 60)}m${Math.round(tempoMedioSeg % 60)}s`
                                              : `${Math.round(tempoMedioSeg)}s`;
                                          })() : '-'}
                                        </td>
                                        <td className={`py-2 px-2 text-right font-bold ${
                                          operador.eficiencia_media >= 100 ? 'text-blue-600' :
                                          operador.eficiencia_media >= 90 ? 'text-green-600' :
                                          operador.eficiencia_media >= 76 ? 'text-yellow-600' : 'text-red-600'
                                        }`}>
                                          {operador.eficiencia_media}%
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                            
                            {/* Tabela Detalhada de Registos */}
                            <div>
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-semibold text-stone-600 flex items-center gap-2">
                                  <FileText className="h-4 w-4" />
                                  Registos Detalhados
                                </h4>
                                {filtroOperadorDetalhe && (
                                  <button
                                    onClick={() => setFiltroOperadorDetalhe('')}
                                    className="text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full flex items-center gap-1 hover:bg-blue-100 transition-colors"
                                    data-testid="limpar-filtro-operador"
                                  >
                                    <User className="h-3 w-3" />
                                    {op.por_operador.find(o => o.operador_id === filtroOperadorDetalhe)?.operador_nome}
                                    <X className="h-3 w-3 ml-1" />
                                  </button>
                                )}
                              </div>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-left text-xs text-stone-500 border-b bg-stone-50">
                                      <th className="py-2 px-2">Data</th>
                                      <th className="py-2 px-2">OF</th>
                                      <th className="py-2 px-2">Operador</th>
                                      <th className="py-2 px-2">Cliente</th>
                                      <th className="py-2 px-2">Modelo</th>
                                      <th className="py-2 px-2">Início</th>
                                      <th className="py-2 px-2">Fim</th>
                                      <th className="py-2 px-2 text-right">Qtd</th>
                                      <th className="py-2 px-2 text-center">Tipo</th>
                                      <th className="py-2 px-2 text-right">Tempo/Peça</th>
                                      <th className="py-2 px-2 text-right">Tempo Ref.</th>
                                      <th className="py-2 px-2 text-right">Eficiência</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {op.por_operador
                                      .filter(operador => !filtroOperadorDetalhe || operador.operador_id === filtroOperadorDetalhe)
                                      .flatMap(operador => 
                                      operador.registos.map((reg, idx) => (
                                        <tr key={`${operador.operador_id}-${idx}`} className="border-b last:border-0 hover:bg-stone-50">
                                          <td className="py-2 px-2 whitespace-nowrap">{reg.data || '-'}</td>
                                          <td className="py-2 px-2 font-medium whitespace-nowrap">
                                            {reg.of_numero ? (
                                              <button
                                                type="button"
                                                onClick={() => navigate(`/gestor/ofs?of=${encodeURIComponent(reg.of_id || '')}&tab=acompanhar&lote=${encodeURIComponent(reg.lote_id || '')}`)}
                                                title="Abrir Acompanhar com este lote pré-selecionado para editar tempos"
                                                className="inline-flex flex-col items-start hover:bg-blue-50 px-1.5 py-0.5 rounded transition-colors"
                                                data-testid={`reg-link-of-${reg.of_id || idx}`}
                                              >
                                                <span className="text-stone-800 font-semibold underline decoration-dotted">{reg.of_numero}</span>
                                                {reg.lote_codigo && (
                                                  <span className="text-[10px] text-blue-600 font-mono">{reg.lote_codigo}</span>
                                                )}
                                              </button>
                                            ) : '-'}
                                          </td>
                                          <td className="py-2 px-2 whitespace-nowrap">
                                            <span className="text-stone-700 font-medium">{operador.operador_nome}</span>
                                          </td>
                                          <td className="py-2 px-2 text-stone-600 truncate max-w-[100px]" title={reg.cliente}>{reg.cliente || '-'}</td>
                                          <td className="py-2 px-2 text-stone-600 truncate max-w-[100px]" title={reg.artigo}>{reg.artigo || '-'}</td>
                                          <td className="py-2 px-2 font-mono text-xs whitespace-nowrap">{reg.hora_inicio || '-'}</td>
                                          <td className="py-2 px-2 font-mono text-xs whitespace-nowrap">{reg.hora_fim || '-'}</td>
                                          <td className="py-2 px-2 text-right font-semibold">{reg.quantidade_produzida}</td>
                                          <td className="py-2 px-2 text-center whitespace-nowrap">
                                            {reg.tipo_conclusao === 'parcial' ? (
                                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-orange-100 text-orange-700">Parcial</span>
                                            ) : reg.tipo_conclusao === 'concluido' ? (
                                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-green-100 text-green-700">Concluído</span>
                                            ) : reg.tipo_conclusao === 'congelado' ? (
                                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-cyan-100 text-cyan-700">Congelado</span>
                                            ) : reg.tipo_conclusao === 'cancelado' ? (
                                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-red-100 text-red-600">Cancelado</span>
                                            ) : (
                                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-stone-100 text-stone-500">-</span>
                                            )}
                                          </td>
                                          <td className="py-2 px-2 text-right text-stone-600 whitespace-nowrap">
                                            {reg.quantidade_produzida > 0 ? (
                                              reg.tempo_peca_seg >= 60 
                                                ? `${Math.floor(reg.tempo_peca_seg / 60)}m ${Math.round(reg.tempo_peca_seg % 60)}s`
                                                : `${Math.round(reg.tempo_peca_seg)}s`
                                            ) : '-'}
                                          </td>
                                          <td className="py-2 px-2 text-right text-stone-500 whitespace-nowrap">
                                            {reg.tempo_referencia_seg > 0 ? (
                                              reg.tempo_referencia_seg >= 60 
                                                ? `${Math.floor(reg.tempo_referencia_seg / 60)}m ${Math.round(reg.tempo_referencia_seg % 60)}s`
                                                : `${Math.round(reg.tempo_referencia_seg)}s`
                                            ) : '-'}
                                          </td>
                                          <td className={`py-2 px-2 text-right font-bold whitespace-nowrap ${
                                            reg.eficiencia >= 100 ? 'text-blue-600' :
                                            reg.eficiencia >= 90 ? 'text-green-600' :
                                            reg.eficiencia >= 76 ? 'text-yellow-600' : 'text-red-600'
                                          }`}>
                                            {Math.round(reg.eficiencia)}%
                                          </td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {relatorioOperacoes.operacoes.length === 0 && (
                      <p className="text-center text-stone-500 py-8">
                        Sem dados de operações para o período selecionado
                      </p>
                    )}
                  </div>
                </Card>
              </div>
            )}
            
            {!relatorioOperacoes && !loading && (
              <Card className="p-12 text-center">
                <Timer className="h-12 w-12 mx-auto text-stone-300 mb-4" />
                <h3 className="text-lg font-semibold text-stone-600 mb-2">Relatório de Operações</h3>
                <p className="text-stone-500 mb-4">
                  Selecione o período e clique em "Gerar Relatório" para ver quanto tempo cada operador demorou em cada operação.
                </p>
              </Card>
            )}
          </div>
        )}


        {/* TAB: TAREFAS PONTUAIS */}
        {activeTab === 'pontuais' && (
          <div className="space-y-4">
            {/* Filtros */}
            <Card className="p-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[180px]">
                  <label className="block text-xs font-medium text-stone-500 mb-1">Operador</label>
                  <select
                    className="w-full h-9 rounded-md border border-stone-200 px-2 text-sm"
                    value={pontuaisFiltroOperador || ''}
                    onChange={(e) => setPontuaisFiltroOperador(e.target.value)}
                    data-testid="pontuais-filtro-operador"
                  >
                    <option value="">Todos operadores</option>
                    {[...new Map(pontuaisData.filter(c => c.operador_id).map(c => [c.operador_id, c.operador_nome])).entries()].map(([id, nome]) => (
                      <option key={id} value={id}>{nome}</option>
                    ))}
                  </select>
                </div>
                <div className="min-w-[180px]">
                  <label className="block text-xs font-medium text-stone-500 mb-1">Operação</label>
                  <select
                    className="w-full h-9 rounded-md border border-stone-200 px-2 text-sm"
                    value={pontuaisFiltroOperacao || ''}
                    onChange={(e) => setPontuaisFiltroOperacao(e.target.value)}
                    data-testid="pontuais-filtro-operacao"
                  >
                    <option value="">Todas operações</option>
                    {[...new Set(pontuaisData.flatMap(c => (c.operacoes || []).map(o => o.operacao_codigo || o.operacao_nome)).filter(Boolean))].sort().map(op => (
                      <option key={op} value={op}>{op}</option>
                    ))}
                  </select>
                </div>
              </div>
            </Card>

            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="tabela-pontuais">
                  <thead>
                    <tr className="text-left text-stone-500 border-b bg-stone-50">
                      <th className="py-2 px-3 font-semibold">Data</th>
                      <th className="py-2 px-3 font-semibold">Hora Início</th>
                      <th className="py-2 px-3 font-semibold">Hora Fim</th>
                      <th className="py-2 px-3 font-semibold">Operador</th>
                      <th className="py-2 px-3 font-semibold">Categoria</th>
                      <th className="py-2 px-3 font-semibold">Tarefa</th>
                      <th className="py-2 px-3 font-semibold">Operações</th>
                      <th className="py-2 px-3 text-right font-semibold">Peças</th>
                      <th className="py-2 px-3 text-right font-semibold">Duração</th>
                      <th className="py-2 px-3 text-right font-semibold">T/Peça</th>
                      <th className="py-2 px-3 text-center font-semibold">Tipo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const filtered = pontuaisData.filter(c => {
                        if (pontuaisFiltroOperador && c.operador_id !== pontuaisFiltroOperador) return false;
                        if (pontuaisFiltroOperacao && !(c.operacoes || []).some(o => (o.operacao_codigo || o.operacao_nome) === pontuaisFiltroOperacao)) return false;
                        return true;
                      });
                      if (filtered.length === 0) return (
                        <tr><td colSpan={11} className="py-8 text-center text-stone-400">Sem conclusões de tarefas pontuais</td></tr>
                      );
                      return filtered.map((c, idx) => {
                        const inicio = c.inicio_em ? new Date(c.inicio_em) : null;
                        const fim = c.concluido_em ? new Date(c.concluido_em) : null;
                        const dataStr = fim ? fim.toLocaleDateString('pt-PT', {day:'2-digit', month:'2-digit', year:'2-digit'}) : '-';
                        const horaIni = inicio ? inicio.toLocaleTimeString('pt-PT', {hour:'2-digit', minute:'2-digit'}) : '-';
                        const horaFim = fim ? fim.toLocaleTimeString('pt-PT', {hour:'2-digit', minute:'2-digit'}) : '-';
                        const durMin = c.duracao_min || c.tempo_minutos || 0;
                        const tppMin = c.tempo_por_peca_min;
                        const tppSeg = c.tempo_por_peca_seg;
                        let tppStr = '-';
                        if (tppMin != null) {
                          if (tppMin < 1 && tppSeg != null) tppStr = `${tppSeg}s`;
                          else tppStr = `${tppMin}min`;
                        }
                        return (
                          <tr key={c.id || idx} className="border-b last:border-0 hover:bg-stone-50">
                            <td className="py-2 px-3 font-mono text-stone-500">{dataStr}</td>
                            <td className="py-2 px-3 font-mono text-stone-600">{horaIni}</td>
                            <td className="py-2 px-3 font-mono text-stone-600">{horaFim}</td>
                            <td className="py-2 px-3 font-medium">{c.operador_nome || '-'}</td>
                            <td className="py-2 px-3">
                              {c.categoria_nome ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: (c.categoria_cor || '#6b7280') + '22', color: c.categoria_cor || '#374151' }}>
                                  {c.categoria_nome}
                                </span>
                              ) : (
                                <span className="text-stone-300">—</span>
                              )}
                            </td>
                            <td className="py-2 px-3">{c.tarefa_nome || '-'}</td>
                            <td className="py-2 px-3 text-xs text-stone-600">
                              {(c.operacoes || []).map(o => o.operacao_codigo || o.operacao_nome).join(', ') || '-'}
                            </td>
                            <td className="py-2 px-3 text-right font-semibold">{c.quantidade || 0}</td>
                            <td className="py-2 px-3 text-right">{durMin > 0 ? `${durMin} min` : '-'}</td>
                            <td className="py-2 px-3 text-right text-stone-600">{tppStr}</td>
                            <td className="py-2 px-3 text-center">
                              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">Pontual</span>
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>
              <PaginationBar
                page={pontuaisPage}
                pageSize={pontuaisPageSize}
                total={pontuaisTotal}
                totalPages={pontuaisTotalPages}
                onPageChange={setPontuaisPage}
                onPageSizeChange={(s) => { setPontuaisPageSize(s); setPontuaisPage(1); }}
                testIdPrefix="pontuais-pagination"
              />
            </Card>
          </div>
        )}


        {/* TAB: ARRANJOS (Lotes ARR concluídos pelos operadores) */}
        {activeTab === 'arranjos' && (
          <div className="space-y-4">
            {/* Resumo + Filtros */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Card className="p-3">
                <p className="text-[10px] uppercase font-bold text-stone-500">Total Registos</p>
                <p className="text-2xl font-bold text-stone-800">{arranjosData.total_registos || 0}</p>
              </Card>
              <Card className="p-3">
                <p className="text-[10px] uppercase font-bold text-orange-600">Total Peças</p>
                <p className="text-2xl font-bold text-orange-700">{arranjosData.total_pecas || 0}</p>
              </Card>
              <Card className="p-3">
                <p className="text-[10px] uppercase font-bold text-blue-600">Total Tempo</p>
                <p className="text-2xl font-bold text-blue-700">{Math.round(arranjosData.total_minutos || 0)} min</p>
              </Card>
              <Card className="p-3 bg-amber-50 border-amber-200">
                <p className="text-[10px] uppercase font-bold text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Nota</p>
                <p className="text-[11px] text-amber-800 leading-tight mt-1">Tempo de arranjos conta para ocupação mas <strong>não</strong> entra em rankings (variabilidade alta).</p>
              </Card>
            </div>

            {/* Filtros */}
            <Card className="p-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[200px]">
                  <label className="block text-xs font-medium text-stone-500 mb-1">Operador</label>
                  <select
                    className="w-full h-9 rounded-md border border-stone-200 px-2 text-sm"
                    value={arranjosFiltroOperador || ''}
                    onChange={(e) => setArranjosFiltroOperador(e.target.value)}
                    data-testid="arranjos-filtro-operador"
                  >
                    <option value="">Todos operadores</option>
                    {[...new Map(arranjosData.items.filter(c => c.operador_id).map(c => [c.operador_id, c.operador_nome])).entries()].map(([id, nome]) => (
                      <option key={id} value={id}>{nome}</option>
                    ))}
                  </select>
                </div>
                <div className="min-w-[200px]">
                  <label className="block text-xs font-medium text-stone-500 mb-1">OF</label>
                  <select
                    className="w-full h-9 rounded-md border border-stone-200 px-2 text-sm"
                    value={arranjosFiltroOF || ''}
                    onChange={(e) => setArranjosFiltroOF(e.target.value)}
                    data-testid="arranjos-filtro-of"
                  >
                    <option value="">Todas as OFs</option>
                    {[...new Map(arranjosData.items.filter(c => c.of_id).map(c => [c.of_id, c.of_numero || '— sem OF —'])).entries()].sort((a,b) => String(a[1]).localeCompare(String(b[1]))).map(([id, num]) => (
                      <option key={id} value={id}>{num}</option>
                    ))}
                  </select>
                </div>
                <div className="ml-auto flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setArranjosColapsados(new Set())} data-testid="arranjos-expandir-todos">Expandir todos</Button>
                  <Button variant="outline" size="sm" onClick={() => {
                    const ids = new Set(arranjosData.items.map(r => r.of_id || '__sem_of__'));
                    setArranjosColapsados(ids);
                  }} data-testid="arranjos-colapsar-todos">Colapsar todos</Button>
                </div>
              </div>
            </Card>

            {/* Tabela */}
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="tabela-arranjos">
                  <thead>
                    <tr className="text-left text-stone-500 border-b bg-stone-50">
                      <th className="py-2 px-3 font-semibold">Data</th>
                      <th className="py-2 px-3 font-semibold">Hora Início</th>
                      <th className="py-2 px-3 font-semibold">Hora Fim</th>
                      <th className="py-2 px-3 font-semibold">Operador</th>
                      <th className="py-2 px-3 font-semibold">Lote ARR</th>
                      <th className="py-2 px-3 font-semibold">Operação</th>
                      <th className="py-2 px-3 font-semibold">OF</th>
                      <th className="py-2 px-3 font-semibold">Modelo / Marca</th>
                      <th className="py-2 px-3 font-semibold">Descritivo</th>
                      <th className="py-2 px-3 text-right font-semibold">Peças</th>
                      <th className="py-2 px-3 text-right font-semibold">Duração</th>
                      <th className="py-2 px-3 text-right font-semibold">T/Peça</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const filtered = arranjosData.items.filter(r => {
                        if (arranjosFiltroOperador && r.operador_id !== arranjosFiltroOperador) return false;
                        if (arranjosFiltroOF && (r.of_id || '') !== arranjosFiltroOF) return false;
                        return true;
                      });
                      if (filtered.length === 0) return (
                        <tr><td colSpan={12} className="py-12 text-center text-stone-400">
                          <Wrench className="h-10 w-10 mx-auto text-stone-200 mb-2" />
                          Sem registos de arranjos no período. Os lotes ARR concluídos pelos operadores aparecerão aqui.
                        </td></tr>
                      );
                      // Agrupar por OF
                      const grupos = new Map();
                      filtered.forEach(r => {
                        const key = r.of_id || '__sem_of__';
                        if (!grupos.has(key)) {
                          grupos.set(key, {
                            of_id: r.of_id,
                            of_numero: r.of_numero || '— sem OF —',
                            of_modelo: r.of_modelo,
                            of_marca: r.of_marca,
                            rows: [],
                            totais: { pecas: 0, duracao_seg: 0, registos: 0, operadores: new Set(), lotes: new Set() }
                          });
                        }
                        const g = grupos.get(key);
                        g.rows.push(r);
                        g.totais.pecas += r.pecas || 0;
                        g.totais.duracao_seg += r.duracao_segundos || 0;
                        g.totais.registos += 1;
                        if (r.operador_id) g.totais.operadores.add(r.operador_id);
                        if (r.lote_codigo) g.totais.lotes.add(r.lote_codigo);
                      });
                      const fmtDur = (s) => {
                        if (!s) return '-';
                        if (s < 60) return `${s}s`;
                        const m = Math.floor(s / 60);
                        const rs = s % 60;
                        if (m < 60) return rs ? `${m}m ${rs}s` : `${m}m`;
                        const h = Math.floor(m / 60);
                        const rm = m % 60;
                        return `${h}h ${rm}min`;
                      };
                      return [...grupos.values()].sort((a,b) => String(a.of_numero).localeCompare(String(b.of_numero))).flatMap(g => {
                        const colapsado = arranjosColapsados.has(g.of_id || '__sem_of__');
                        const headerRow = (
                          <tr
                            key={`hdr-${g.of_id || 'sem'}`}
                            className="bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border-t-2 border-b-2 border-amber-300 cursor-pointer hover:bg-amber-100 transition"
                            onClick={() => {
                              const k = g.of_id || '__sem_of__';
                              setArranjosColapsados(prev => {
                                const novo = new Set(prev);
                                if (novo.has(k)) novo.delete(k); else novo.add(k);
                                return novo;
                              });
                            }}
                            data-testid={`arranjos-grupo-of-${g.of_id || 'sem'}`}
                          >
                            <td colSpan={12} className="py-2 px-3">
                              <div className="flex items-center gap-3 flex-wrap">
                                <span className="text-stone-600 font-bold text-xs">{colapsado ? '▶' : '▼'}</span>
                                <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700">OF</span>
                                <span className="font-mono font-extrabold text-stone-900 text-base">{g.of_numero}</span>
                                {(g.of_modelo || g.of_marca) && (
                                  <span className="text-xs text-stone-600">{g.of_modelo} <span className="text-stone-400">·</span> {g.of_marca}</span>
                                )}
                                <span className="ml-auto flex items-center gap-3 text-xs">
                                  <span className="px-2 py-0.5 rounded bg-white border border-stone-200 font-semibold text-stone-700">{g.totais.registos} reg.</span>
                                  <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 font-bold" title="Lotes ARR">{g.totais.lotes.size} lotes</span>
                                  <span className="px-2 py-0.5 rounded bg-orange-100 text-orange-800 font-bold">{g.totais.pecas} pçs</span>
                                  <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 font-bold">{fmtDur(g.totais.duracao_seg)}</span>
                                  <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold">{g.totais.operadores.size} op.</span>
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                        if (colapsado) return [headerRow];
                        const rowsJsx = g.rows.map((r) => {
                          const dataStr = r.data ? new Date(r.data).toLocaleDateString('pt-PT', {day:'2-digit', month:'2-digit', year:'2-digit'}) : '-';
                          const tppStr = r.tempo_por_peca_seg != null
                            ? (r.tempo_por_peca_seg < 60 ? `${r.tempo_por_peca_seg}s` : `${Math.round(r.tempo_por_peca_seg / 60)}min`)
                            : '-';
                          return (
                            <tr key={r.id} className="border-b last:border-0 hover:bg-stone-50">
                              <td className="py-2 px-3 font-mono text-stone-500">{dataStr}</td>
                              <td className="py-2 px-3 font-mono text-stone-600">{r.hora_inicio}</td>
                              <td className="py-2 px-3 font-mono text-stone-600">{r.hora_fim}</td>
                              <td className="py-2 px-3 font-medium">{r.operador_nome}</td>
                              <td className="py-2 px-3">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
                                  {r.lote_codigo}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-xs">
                                <div className="flex items-center gap-1.5">
                                  {r.is_mae && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-extrabold bg-rose-600 text-white" title="Operação Mãe">MÃE</span>
                                  )}
                                  <span className="font-mono font-semibold text-stone-700">{r.operacao_codigo}</span>
                                  <span className="text-stone-500 truncate max-w-[140px]" title={r.operacao_nome}>{r.operacao_nome}</span>
                                </div>
                              </td>
                              <td className="py-2 px-3 font-mono text-xs">{r.of_numero}</td>
                              <td className="py-2 px-3 text-xs text-stone-600">
                                <div className="truncate max-w-[160px]" title={`${r.of_modelo} / ${r.of_marca}`}>
                                  {r.of_modelo} <span className="text-stone-400">·</span> {r.of_marca}
                                </div>
                              </td>
                              <td className="py-2 px-3 text-xs text-stone-700">
                                <div className="truncate max-w-[280px]" title={r.descritivo}>{r.descritivo || <span className="text-stone-300 italic">— sem descritivo —</span>}</div>
                              </td>
                              <td className="py-2 px-3 text-right font-semibold">{r.pecas}</td>
                              <td className="py-2 px-3 text-right">{fmtDur(r.duracao_segundos || 0)}</td>
                              <td className="py-2 px-3 text-right text-stone-600">{tppStr}</td>
                            </tr>
                          );
                        });
                        return [headerRow, ...rowsJsx];
                      });
                    })()}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}


        {/* TAB: SUSPEITOS (Registos com eficiência anómala + exclusão manual do ranking) */}
        {activeTab === 'suspeitos' && (
          <div className="space-y-4" data-testid="tab-suspeitos">
            <LimitesSuspeitosCard onChanged={() => { setSuspeitosPage(1); loadSuspeitos(); }} />

            <Card className="p-3">
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={suspeitosIncluirExcluidos}
                  onChange={(e) => { setSuspeitosIncluirExcluidos(e.target.checked); setSuspeitosPage(1); }}
                  className="rounded"
                  data-testid="suspeitos-incluir-excluidos"
                />
                <span>Mostrar também registos excluídos manualmente</span>
              </label>
            </Card>

            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="tabela-suspeitos">
                  <thead>
                    <tr className="text-left text-stone-500 border-b bg-stone-50">
                      <th className="py-2 px-3 font-semibold">Data</th>
                      <th className="py-2 px-3 font-semibold">Operador</th>
                      <th className="py-2 px-3 font-semibold">OF</th>
                      <th className="py-2 px-3 font-semibold">Operação</th>
                      <th className="py-2 px-3 text-right font-semibold">Peças</th>
                      <th className="py-2 px-3 text-right font-semibold">T. Prod</th>
                      <th className="py-2 px-3 text-right font-semibold">Efic.</th>
                      <th className="py-2 px-3 font-semibold">Motivos</th>
                      <th className="py-2 px-3 text-center font-semibold">Estado</th>
                      <th className="py-2 px-3 text-center font-semibold">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suspeitosData.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="py-8 text-center text-stone-400">
                          {loading ? 'A carregar…' : 'Sem registos suspeitos no período selecionado'}
                        </td>
                      </tr>
                    ) : (
                      suspeitosData.map((r) => {
                        const inicio = r.inicio ? new Date(r.inicio) : null;
                        const dataStr = inicio ? inicio.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-';
                        const horaStr = inicio ? inicio.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' }) : '';
                        const tempoProdMin = Math.round((r.tempo_produtivo_segundos || 0) / 6) / 10;
                        const efic = Math.round((r.eficiencia_percentual || 0) * 10) / 10;
                        const excluido = !!r.excluido_ranking;
                        const motivos = r.suspeito_motivos || [];
                        const updating = suspeitoUpdating === r.id;
                        return (
                          <tr key={r.id} className={`border-b last:border-0 hover:bg-stone-50 ${excluido ? 'opacity-60' : ''}`} data-testid={`suspeito-row-${r.id}`}>
                            <td className="py-2 px-3 font-mono text-stone-500 whitespace-nowrap">
                              {dataStr}
                              <div className="text-[10px] text-stone-400">{horaStr}</div>
                            </td>
                            <td className="py-2 px-3 font-medium">{r.operador_nome || '-'}</td>
                            <td className="py-2 px-3">
                              <div className="font-semibold text-navy">{r.of_numero || '-'}</div>
                              <div className="text-[10px] text-stone-500">{r.of_cliente || ''}</div>
                            </td>
                            <td className="py-2 px-3 text-xs">{r.operacao_codigo || r.operacao_nome || '-'}</td>
                            <td className="py-2 px-3 text-right font-semibold">{r.quantidade_produzida || 0}</td>
                            <td className="py-2 px-3 text-right text-stone-600">{tempoProdMin} min</td>
                            <td className={`py-2 px-3 text-right font-bold ${efic > (suspeitosLimites.efic_max ?? 250) ? 'text-red-600' : efic < (suspeitosLimites.efic_min ?? 20) ? 'text-orange-600' : 'text-stone-600'}`}>
                              {efic > 0 ? `${efic}%` : '-'}
                            </td>
                            <td className="py-2 px-3">
                              <div className="flex flex-wrap gap-1">
                                {motivos.length > 0 ? motivos.map((m, i) => (
                                  <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                                    {m}
                                  </span>
                                )) : <span className="text-stone-300 text-xs">—</span>}
                              </div>
                            </td>
                            <td className="py-2 px-3 text-center">
                              {excluido ? (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">Excluído</span>
                              ) : r.suspeito ? (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Auto-flagged</span>
                              ) : (
                                <span className="text-xs text-stone-400">—</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-center">
                              <button
                                onClick={() => toggleExclusaoRanking(r.id, excluido)}
                                disabled={updating}
                                className={`text-xs px-2 py-1 rounded border font-medium transition-colors ${excluido
                                  ? 'bg-white border-stone-300 text-stone-700 hover:bg-stone-100'
                                  : 'bg-red-600 border-red-600 text-white hover:bg-red-700'}`}
                                data-testid={`suspeito-toggle-${r.id}`}
                                title={excluido ? 'Voltar a incluir no ranking' : 'Excluir do ranking'}
                              >
                                {updating ? '...' : excluido ? 'Re-incluir' : 'Excluir'}
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              <PaginationBar
                page={suspeitosPage}
                pageSize={suspeitosPageSize}
                total={suspeitosTotal}
                totalPages={suspeitosTotalPages}
                onPageChange={setSuspeitosPage}
                onPageSizeChange={(s) => { setSuspeitosPageSize(s); setSuspeitosPage(1); }}
                testIdPrefix="suspeitos-pagination"
              />
            </Card>
          </div>
        )}


        {/* TAB: MELHORES OPERADORES POR OPERAÇÃO */}
        {activeTab === 'melhores' && (
          <div className="space-y-4">
            {/* Filtros de Categoria + PDF */}
            {rankings?.melhores_por_operacao && rankings.melhores_por_operacao.length > 0 && (() => {
              // Agregar categorias únicas com cor + contagem
              const catMap = new Map();
              rankings.melhores_por_operacao.forEach(op => {
                const codigo = op.categoria_codigo || '__sem__';
                if (!catMap.has(codigo)) {
                  catMap.set(codigo, {
                    codigo,
                    nome: op.categoria_nome || (codigo === '__sem__' ? 'Sem categoria' : codigo),
                    cor: op.categoria_cor || '#64748b',
                    count: 0
                  });
                }
                catMap.get(codigo).count += 1;
              });
              const categorias = Array.from(catMap.values()).sort((a, b) => a.codigo.localeCompare(b.codigo));
              const totalOps = rankings.melhores_por_operacao.length;
              return (
                <Card className="p-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold text-stone-600 mr-1">Categoria:</span>
                      {/* Botão Todas */}
                      <button
                        onClick={() => setTopOpsCategoriaFiltro('')}
                        data-testid="topops-cat-todas"
                        className={`text-xs px-3 py-1.5 rounded-full border-2 font-semibold transition-all ${
                          topOpsCategoriaFiltro === ''
                            ? 'bg-navy text-white border-navy shadow-sm'
                            : 'bg-white text-stone-600 border-stone-200 hover:border-stone-400'
                        }`}
                      >
                        Todas <span className="opacity-70 ml-1">({totalOps})</span>
                      </button>
                      {categorias.map(cat => {
                        const isActive = topOpsCategoriaFiltro === cat.codigo;
                        return (
                          <button
                            key={cat.codigo}
                            onClick={() => setTopOpsCategoriaFiltro(isActive ? '' : cat.codigo)}
                            data-testid={`topops-cat-${cat.codigo}`}
                            className={`text-xs px-3 py-1.5 rounded-full border-2 font-bold transition-all uppercase tracking-wide`}
                            style={isActive ? {
                              backgroundColor: cat.cor,
                              color: '#fff',
                              borderColor: cat.cor,
                              boxShadow: `0 2px 8px ${cat.cor}55`
                            } : {
                              backgroundColor: cat.cor + '15',
                              color: cat.cor,
                              borderColor: cat.cor + '55'
                            }}
                            title={cat.nome}
                          >
                            {cat.codigo === '__sem__' ? 'Sem cat.' : cat.codigo}
                            <span className="ml-1.5 font-normal opacity-80 normal-case">({cat.count})</span>
                          </button>
                        );
                      })}
                    </div>
                    <Button variant="outline" size="sm" onClick={exportPDF} disabled={exporting} data-testid="export-pdf-melhores">
                      <FileText className="h-4 w-4 mr-1" /> PDF
                    </Button>
                  </div>
                </Card>
              );
            })()}
            {(!rankings?.melhores_por_operacao || rankings.melhores_por_operacao.length === 0) && (
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={exportPDF} disabled={exporting} data-testid="export-pdf-melhores">
                  <FileText className="h-4 w-4 mr-1" /> PDF
                </Button>
              </div>
            )}
            {loading && (
              <div className="text-center py-4 text-stone-400 text-sm">A carregar...</div>
            )}
            
            {/* Grid de Melhores por Operação */}
            {rankings?.melhores_por_operacao && rankings.melhores_por_operacao.length > 0 ? (() => {
              const opsFiltradas = topOpsCategoriaFiltro
                ? rankings.melhores_por_operacao.filter(op =>
                    (op.categoria_codigo || '__sem__') === topOpsCategoriaFiltro
                  )
                : rankings.melhores_por_operacao;
              if (opsFiltradas.length === 0) {
                return (
                  <Card className="p-6 text-center">
                    <p className="text-stone-500 text-sm">Sem operações para a categoria selecionada</p>
                  </Card>
                );
              }
              return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {opsFiltradas.map((op) => (
                  <Card key={op.operacao_id} className="p-3 hover:shadow-md transition-shadow">
                    <div className="mb-2">
                      <div className="flex items-start gap-2 mb-1">
                        <Package className="h-4 w-4 text-navy mt-0.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-stone-900 text-sm leading-tight" title={op.operacao_nome}>
                            {op.operacao_codigo && (
                              <span className="text-stone-500 font-mono mr-1.5">{op.operacao_codigo}</span>
                            )}
                            {op.operacao_nome}
                          </div>
                          {op.categoria_codigo && (
                            <span
                              className="inline-block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide"
                              style={{
                                backgroundColor: (op.categoria_cor || '#64748b') + '22',
                                color: op.categoria_cor || '#64748b',
                                border: `1px solid ${op.categoria_cor || '#64748b'}55`
                              }}
                              title={op.categoria_nome}
                            >
                              {op.categoria_codigo}
                              {op.categoria_nome && <span className="ml-1 font-normal opacity-80">{op.categoria_nome}</span>}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {op.operadores.length === 0 ? (
                      <p className="text-xs text-stone-400 text-center py-2">Sem dados</p>
                    ) : (
                      <div className="space-y-2">
                        {op.operadores.map((operador, idx) => (
                          <div 
                            key={operador.operador_id} 
                            className={`p-2 rounded ${
                              idx === 0 ? 'bg-gradient-to-r from-amber-50 to-white border border-amber-200' : 'bg-stone-50'
                            }`}
                          >
                            {/* Nome e Eficiência */}
                            <div className="flex items-center justify-between text-xs mb-1">
                              <div className="flex items-center gap-1.5">
                                {getMedalIcon(idx + 1)}
                                <span className={idx === 0 ? 'font-semibold' : ''}>{operador.nome}</span>
                              </div>
                              <span className={`font-bold ${
                                operador.eficiencia_media >= 100 ? 'text-blue-600' : 
                                operador.eficiencia_media >= 80 ? 'text-green-600' : 'text-amber-600'
                              }`}>
                                {operador.eficiencia_media}%
                              </span>
                            </div>
                            
                            {/* Tempos e Peças */}
                            <div className="flex items-center gap-3 text-[10px] text-stone-500">
                              <span title="Tempo Previsto">
                                <Timer className="h-3 w-3 inline mr-0.5" />
                                Prev: {formatTempo(operador.tempo_previsto || 0)}
                              </span>
                              <span title="Tempo Real">
                                Real: {formatTempo(operador.tempo_total)}
                              </span>
                              <span>{operador.pecas_total}pçs</span>
                            </div>
                            
                            {/* OFs */}
                            {operador.ofs && operador.ofs.length > 0 && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {operador.ofs.map((of, i) => (
                                  <span key={`of-${of}-${i}`} className="text-[9px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                                    OF {of}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
              );
            })() : (
              <Card className="p-8 text-center">
                <Trophy className="h-12 w-12 mx-auto text-stone-200 mb-3" />
                <p className="text-stone-500">Sem dados de rankings para o período seleccionado</p>
              </Card>
            )}
          </div>
        )}

        {/* TAB: RANKINGS */}
        {activeTab === 'rankings' && (
          <div className="space-y-6">
            {/* Quick range presets — vista diária / semanal / mensal */}
            <Card className="p-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-stone-600 mr-1">Período:</span>
                  {[
                    { id: 'hoje', label: 'Hoje' },
                    { id: 'semana', label: 'Esta Semana' },
                    { id: '7d', label: 'Últimos 7 dias' },
                    { id: 'mes', label: 'Este Mês' },
                    { id: '30d', label: 'Últimos 30 dias' },
                  ].map(p => (
                    <button
                      key={p.id}
                      onClick={() => {
                        const today = new Date();
                        const fmt = (d) => d.toISOString().slice(0, 10);
                        let ini, fim;
                        if (p.id === 'hoje') { ini = fim = fmt(today); }
                        else if (p.id === 'semana') {
                          // Segunda da semana actual
                          const d = new Date(today);
                          const diff = (d.getDay() + 6) % 7;
                          d.setDate(d.getDate() - diff);
                          ini = fmt(d); fim = fmt(today);
                        } else if (p.id === '7d') {
                          const d = new Date(today); d.setDate(d.getDate() - 6);
                          ini = fmt(d); fim = fmt(today);
                        } else if (p.id === 'mes') {
                          const d = new Date(today.getFullYear(), today.getMonth(), 1);
                          ini = fmt(d); fim = fmt(today);
                        } else if (p.id === '30d') {
                          const d = new Date(today); d.setDate(d.getDate() - 29);
                          ini = fmt(d); fim = fmt(today);
                        }
                        setDataInicio(ini); setDataFim(fim);
                        loadRankings(ini, fim);  // passar datas diretamente, evita closure stale
                      }}
                      className="text-xs px-3 py-1.5 rounded-lg bg-stone-100 hover:bg-emerald-100 hover:text-emerald-700 font-medium transition-colors"
                      data-testid={`ranking-preset-${p.id}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                {(() => {
                  if (!dataInicio || !dataFim) return null;
                  const d1 = new Date(dataInicio), d2 = new Date(dataFim);
                  const dias = Math.max(1, Math.round((d2 - d1) / 86400000) + 1);
                  return (
                    <div className="text-xs text-stone-600">
                      {dias > 1 ? <span className="font-bold text-emerald-700">📊 Média de {dias} dias</span> : <span className="text-stone-500">📅 Vista de 1 dia</span>}
                    </div>
                  );
                })()}
              </div>

              {/* Filtro de operadores (multi-select via checkboxes) */}
              <div className="mt-3 pt-3 border-t border-stone-100">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <span className="text-xs font-semibold text-stone-600">Operadores no ranking:</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setRankingOperadoresFiltrados([]); loadRankings(null, null, []); }}
                      className="text-[10px] px-2 py-0.5 rounded bg-stone-100 hover:bg-stone-200 font-medium"
                      data-testid="ranking-todos-operadores"
                    >Todos</button>
                    <button
                      onClick={() => { const ids = operadores.map(o => o.id); setRankingOperadoresFiltrados(ids); loadRankings(null, null, ids); }}
                      className="text-[10px] px-2 py-0.5 rounded bg-stone-100 hover:bg-stone-200 font-medium"
                      data-testid="ranking-marcar-todos"
                    >Marcar Todos</button>
                    <button
                      onClick={() => { setRankingOperadoresFiltrados(['__none__']); loadRankings(null, null, ['__none__']); }}
                      className="text-[10px] px-2 py-0.5 rounded bg-stone-100 hover:bg-stone-200 font-medium"
                      data-testid="ranking-desmarcar-todos"
                    >Nenhum</button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                  {operadores.filter(o => o.ativo !== false).map(op => {
                    const todosMode = rankingOperadoresFiltrados.length === 0;
                    const marcado = todosMode || rankingOperadoresFiltrados.includes(op.id);
                    return (
                      <button
                        key={op.id}
                        onClick={() => {
                          let novo;
                          if (todosMode) {
                            // Estava em "todos"; clicar desmarca este (passa a lista explícita sem ele)
                            novo = operadores.filter(o => o.id !== op.id && o.ativo !== false).map(o => o.id);
                          } else if (marcado) {
                            novo = rankingOperadoresFiltrados.filter(id => id !== op.id);
                            if (novo.length === 0) novo = ['__none__'];
                          } else {
                            novo = [...rankingOperadoresFiltrados.filter(id => id !== '__none__'), op.id];
                          }
                          setRankingOperadoresFiltrados(novo);
                          loadRankings(null, null, novo);
                        }}
                        className={`text-[10px] px-2 py-0.5 rounded border transition-colors ${
                          marcado
                            ? 'bg-emerald-100 border-emerald-300 text-emerald-700 font-semibold'
                            : 'bg-white border-stone-300 text-stone-500 hover:bg-stone-50'
                        }`}
                        data-testid={`ranking-op-${op.id}`}
                        title={marcado ? 'Clique para remover do ranking' : 'Clique para adicionar ao ranking'}
                      >
                        {marcado ? '✓ ' : ''}{op.nome_completo || op.nome || op.username}
                      </button>
                    );
                  })}
                </div>
                {rankingOperadoresFiltrados.length > 0 && rankingOperadoresFiltrados[0] !== '__none__' && (
                  <p className="text-[10px] text-emerald-700 mt-1.5">A mostrar {rankingOperadoresFiltrados.length} operador(es) selecionado(s)</p>
                )}
              </div>
            </Card>
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={exportPDF} disabled={exporting} data-testid="export-pdf-rankings">
                <FileText className="h-4 w-4 mr-1" /> PDF
              </Button>
            </div>
            {loading ? (
              <div className="text-center py-12 text-stone-500">A carregar rankings...</div>
            ) : rankings && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Ranking Eficiência */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Target className="h-5 w-5 text-blue-600" />
                    Ranking de Eficiência
                  </h3>
                  <div className="space-y-3">
                    {rankings.ranking_eficiencia.slice(0, 10).map((op) => (
                      <button
                        type="button"
                        key={op.id}
                        onClick={() => {
                          setActiveTab('operador');
                          setSelectedOperador(op.id);
                          setTimeout(() => loadRelatorioOperador(), 100);
                        }}
                        className={`w-full text-left flex items-center justify-between p-3 rounded-xl transition-all hover:scale-[1.01] hover:shadow ${
                          op.posicao <= 3 ? 'bg-gradient-to-r from-amber-50 to-white border border-amber-200' : 'bg-stone-50 hover:bg-stone-100'
                        }`}
                        data-testid={`ranking-eficiencia-${op.id}`}
                        title="Clique para ver detalhe deste operador"
                      >
                        <div className="flex items-center gap-3">
                          {getMedalIcon(op.posicao)}
                          <div>
                            <p className="font-semibold">{op.nome}</p>
                            <p className="text-xs text-stone-500">{op.total_operacoes} operações</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-xl font-bold ${
                            op.eficiencia_media >= 100 ? 'text-blue-600' : 'text-green-600'
                          }`}>{op.eficiencia_media}%</p>
                          <p className="text-xs text-stone-500">{op.total_pecas} peças</p>
                        </div>
                      </button>
                    ))}
                    {rankings.ranking_eficiencia.length === 0 && (
                      <p className="text-center text-stone-500 py-4">Sem dados para o período selecionado</p>
                    )}
                  </div>
                </Card>

                {/* Ranking Ocupação */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Percent className="h-5 w-5 text-green-600" />
                    Ranking de Taxa de Ocupação
                  </h3>
                  <div className="space-y-3">
                    {rankings.ranking_ocupacao.slice(0, 10).map((op) => (
                      <button
                        type="button"
                        key={op.id}
                        onClick={() => {
                          setActiveTab('operador');
                          setSelectedOperador(op.id);
                          setTimeout(() => loadRelatorioOperador(), 100);
                        }}
                        className={`w-full text-left flex items-center justify-between p-3 rounded-xl transition-all hover:scale-[1.01] hover:shadow ${
                          op.posicao <= 3 ? 'bg-gradient-to-r from-green-50 to-white border border-green-200' : 'bg-stone-50 hover:bg-stone-100'
                        }`}
                        data-testid={`ranking-ocupacao-${op.id}`}
                        title="Clique para ver detalhe deste operador"
                      >
                        <div className="flex items-center gap-3">
                          {getMedalIcon(op.posicao)}
                          <div>
                            <p className="font-semibold">{op.nome}</p>
                            <p className="text-xs text-stone-500">{formatarTempo(op.tempo_operacoes_horas)} em operações</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-green-600">{op.taxa_ocupacao}%</p>
                          <p className="text-xs text-stone-500">ocupação</p>
                        </div>
                      </button>
                    ))}
                    {rankings.ranking_ocupacao.length === 0 && (
                      <p className="text-center text-stone-500 py-4">Sem dados para o período selecionado</p>
                    )}
                  </div>
                </Card>

                {/* Estatísticas Gerais */}
                {rankings.estatisticas && (
                  <Card className="p-6 lg:col-span-2">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-navy" />
                      Estatísticas Gerais
                    </h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-4 bg-stone-50 rounded-xl">
                        <p className="text-3xl font-bold text-navy">{rankings.estatisticas.total_operadores_ativos}</p>
                        <p className="text-sm text-stone-600">Operadores com Actividade</p>
                      </div>
                      <div className="text-center p-4 bg-blue-50 rounded-xl">
                        <p className="text-3xl font-bold text-blue-600">{rankings.estatisticas.eficiencia_media_geral}%</p>
                        <p className="text-sm text-stone-600">Eficiência Média Geral</p>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-xl">
                        <p className="text-3xl font-bold text-green-600">{rankings.estatisticas.ocupacao_media_geral}%</p>
                        <p className="text-sm text-stone-600">Ocupação Média Geral</p>
                      </div>
                    </div>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}
        {activeTab === 'ranking_final' && <RankingFinal />}
        </div>{/* End relatorios-content */}
      </div>
    </GestorLayout>
  );
}
